import React, { useState, useEffect, useRef, useMemo, Suspense, useCallback } from 'react';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Modal,
  Select,
  Slider,
  TextField,
  Tooltip,
  Typography,
  Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import CodeIcon from '@mui/icons-material/Code';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { MAPBOX_ACCESS_TOKEN } from 'src/utils/keys';
import { dummyBookingComData, dummyLTRData } from './const';
import { EditMode, KpiPercentileType, ReportTypes, UserReportAccessTypes } from '../../propTypes';
import {
  COLOR_GREEN,
  COLOR_PRIMARY,
  COLOR_RED,
  exchangeRates,
  MAILCHIMP_FIELDS,
  monthNames,
  ALL_COUNTRY,
  CALCULATOR_API_URL,
  HEADER_HEIGHT,
  KPI_NAV_BAR_Z_INDEX,
  sub_group_legends,
} from 'src/utils/const';
import {
  calculateHistoricalTrends,
  getBestMarketBlogURLByCountry,
  getCountryFromReportData,
  getDummyMarketURL,
  getSeasonality,
  getSeasonalityBarColor,
  getUserPreferredCurrency,
  setUserDataToMailChimp,
  handleImageInput,
  parseMessageHistoricalData,
  convertCurrency,
  formatCurrency,
  formatDisplayCurrency,
  getGradeColor,
  getGradeAGradient,
  formatMarketName,
  getMapboxStaticPropertyPreviewUrl,
  beautifyDate,
} from 'src/utils/functions';
import RepotPageInputSection from './Components/ReportPageInputSection';
import AutohideSnackbar from 'src/App/Dashboards/Market/Components/Alerts/AutohideSnackbar';

import { reportPageStyles as styles } from './styles';
import {
  COLOR_GREY_400,
  COLOR_GREY_600,
  COLOR_PRIMARY_1,
  COLOR_TEXT_1,
  COLOR_TEXT_4,
  COLOR_TEXT_GREY,
} from 'src/utils/colors';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import { User } from 'src/interfaces/iuser';
import ShareLinkModal from 'src/App/Dashboards/Market/Components/Modals/ShareLinkModal';
import TooltipNew from 'src/App/Dashboards/Market/Components/Tooltips/TooltipNew';
import TutorialModal from 'src/App/Dashboards/Market/Modals/TutorialModal';
import {
  checkIfUserAllowedToEditReport,
  getLtmDates,
  initializeInputEditState,
  initializeMetricsState,
  processLtrData,
  getIsEditedKpiAvailable,
} from '../../helper';
import { useHistory } from 'react-router-dom';
import { callApi } from 'src/core/callApi';
import { convertApiCurrency } from '../../helper';
import { getHistoricalData } from 'src/App/Dashboards/Market/Pages/OverviewPage/helper';
import { logAmplitudeEvent } from 'src/utils/analytics';
import {
  financialInputs,
  marketMetrics,
  performanceIndicators,
} from '../../configs/FinancialKPIConfig';
import { computeListingPremiumBreakdown } from '../../configs/ListingPremiumConfig';
import { CALCULATOR_MARKET_DATA_TOOLTIP } from 'src/utils/kpiDefinitions';
import { CalculatorSection, OtaMetricsSection } from './Components';
import InlineCalculatorForm from './Components/InlineCalculatorForm';
import DisabledBlurCard from '../../UtilityComponents/BlurCard';
import { Search, SubscriptionModalOrigin, SubscriptionPlan } from 'src/interfaces/idashboard';
import DownloadReportModal from 'src/App/Dashboards/Market/Components/Modals/DownloadReportModal';
import ExportExcelModal from 'src/App/Dashboards/Market/Components/Modals/ExportExcelModal';
import DEMO_SEASONALITY_CHART from 'src/App/assets/demo_seasonality.svg';
import OpenInNew from '@mui/icons-material/OpenInNew';
import Top10Markets from 'src/App/Dashboards/Market/Components/Top10Markets';
import { LoadingCard } from 'src/App/Dashboards/Market/Components';
import { getBookingComData, getLTRCompData } from '../../api';
import RealtorForm from '../../UtilityComponents/RealtorForm/RealtorForm';
import BookingComMapImage1 from 'src/App/assets/map_booking_com.jpg';
import BookingComMapImage2 from 'src/App/assets/booking_com_map_2.jpg';
import OtaComMapImage from 'src/App/assets/ota_markers_map.jpg';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { exportReportToExcel } from '../../utils/exportToExcel';
import { buildReportFilterUpdatePayload } from './buildReportFilterUpdatePayload';

/** Clears fixed mobile app nav (Overview / Calculator / Explorer) in Sidebar.tsx */
const MOBILE_BOTTOM_NAV_CLEARANCE = 'calc(5.75rem + env(safe-area-inset-bottom, 0px))';

function calculatorReportWelcomeStorageKey(email: string) {
  return `airbtics_calculator_report_welcome_v1_${encodeURIComponent(email)}`;
}

/** Comps async build: poll with backoff (sequential READs, no overlap). */
const COMPS_POLL_INITIAL_MS = 2000;
const COMPS_POLL_BACKOFF_MULTIPLIER = 1.8;
const COMPS_POLL_MAX_DELAY_MS = 30000;
const COMPS_POLL_MAX_ATTEMPTS = 12;

const PropertyNoteVisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const HERO_MAP_ZOOM_DEFAULT = 14;
const HERO_MAP_W = 800;
const HERO_MAP_H = 400;
const HERO_MAP_ZOOM_MIN = 10;
const HERO_MAP_ZOOM_MAX = 18;

/** Compute zoom so the hexagon fills ~25% of map width; leaves margin when hexagon is off-center. */
function computeHeroMapZoom(neighborhoodInnerRadiusKm: number | null | undefined): number {
  if (neighborhoodInnerRadiusKm == null || neighborhoodInnerRadiusKm <= 0) return HERO_MAP_ZOOM_DEFAULT;
  const r = Number(neighborhoodInnerRadiusKm);
  if (Number.isNaN(r)) return HERO_MAP_ZOOM_DEFAULT;
  const diameterKm = 2 * r;
  const targetPx = 200; // ~25% of 800px width (conservative; hex often offset from center)
  const pxPerKmAtZoom14 = 105;
  const diameterPxAt14 = diameterKm * pxPerKmAtZoom14;
  const zoom = HERO_MAP_ZOOM_DEFAULT + Math.log2(targetPx / diameterPxAt14);
  return Math.round(Math.max(HERO_MAP_ZOOM_MIN, Math.min(HERO_MAP_ZOOM_MAX, zoom)));
}

function lngLatToPixel(
  lng: number, lat: number,
  centerLng: number, centerLat: number,
  zoom: number, width: number, height: number
): { x: number; y: number } {
  const scale = (256 / (2 * Math.PI)) * Math.pow(2, zoom);
  const toX = (v: number) => scale * (v * Math.PI / 180 + Math.PI);
  const toY = (v: number) => scale * (Math.PI - Math.log(Math.tan(Math.PI / 4 + (v * Math.PI / 180) / 2)));
  const cx = toX(centerLng);
  const cy = toY(centerLat);
  return {
    x: (toX(lng) - cx) + width / 2,
    y: (toY(lat) - cy) + height / 2,
  };
}

type NeighborhoodGeom = { type?: string; coordinates?: unknown };

function neighborhoodGeometryToPolygonPointStrings(
  geom: NeighborhoodGeom,
  centerLng: number,
  centerLat: number,
  zoom: number
): string[] {
  if (!geom?.type || !geom.coordinates) return [];
  const rings: number[][][] = [];
  if (geom.type === 'Polygon' && Array.isArray((geom.coordinates as number[][][])[0])) {
    rings.push((geom.coordinates as number[][][])[0]);
  } else if (geom.type === 'MultiPolygon' && Array.isArray(geom.coordinates)) {
    for (const poly of geom.coordinates as number[][][][]) {
      if (poly?.[0]) rings.push(poly[0]);
    }
  }
  const out: string[] = [];
  for (const ring of rings) {
    const parts: string[] = [];
    for (const pt of ring) {
      if (!Array.isArray(pt) || pt.length < 2) continue;
      const lng = Number(pt[0]);
      const lat = Number(pt[1]);
      if (Number.isNaN(lng) || Number.isNaN(lat)) continue;
      const { x, y } = lngLatToPixel(lng, lat, centerLng, centerLat, zoom, HERO_MAP_W, HERO_MAP_H);
      parts.push(`${x},${y}`);
    }
    if (parts.length >= 3) out.push(parts.join(' '));
  }
  return out;
}

/** Teal hex shown beside “(on map)” in revenue subtitle — reused in hero map legend. */
function NeighbourhoodMapHexIcon(props: { size?: number }) {
  const s = props.size ?? 12;
  return (
    <Box
      component="svg"
      width={s}
      height={s}
      viewBox="0 0 100 100"
      sx={{ flexShrink: 0, display: 'block' }}
      aria-hidden
    >
      <polygon points="50,3 97,25 97,75 50,97 3,75 3,25" fill="#037A90" opacity={0.85} />
    </Box>
  );
}

/** Slate map pin for subject property (distinct from teal neighbourhood hex on hero static map). */
function HeroMapSubjectPin(props: { size: number }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      sx={{ width: props.size, height: props.size, display: 'block', flexShrink: 0 }}
      aria-hidden
    >
      <path
        fill="#1f2937"
        stroke="#fff"
        strokeWidth={1.2}
        vectorEffect="non-scaling-stroke"
        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
      />
    </Box>
  );
}

/**
 * Inline-editable hero stat tile for Occupancy Rate and Nightly Rate.
 * Mirrors the slider-edit pattern from `CalculatorKPICard` (25th/50th/75th marks)
 * while preserving the hero tile's tri-accent visual design.
 *
 * Edits are applied via `onApply` (caller holds the override state);
 * the component remains fully controlled by the `value` prop.
 */
function HeroEditableStat({
  label,
  unit,
  value,
  displayValue,
  accent,
  marketText,
  percentiles,
  isOverridden,
  canEdit,
  onApply,
  onReset,
}: {
  label: string;
  /** '%' for occupancy, '$' for ADR. Controls input suffix/prefix and formatting. */
  unit: '%' | '$';
  /** Current effective numeric value in base units (0-100 for %, base-currency number for $). */
  value: number | null;
  /** Display string (already formatted with currency symbol etc.) shown in non-edit mode. */
  displayValue: string;
  accent: string;
  marketText: string | null;
  /** 25th/50th/75th anchor values in the same base units as `value`. */
  percentiles: { p25: number | null; p50: number | null; p75: number | null };
  isOverridden: boolean;
  canEdit: boolean;
  onApply: (v: number) => void;
  onReset: () => void;
}) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState<number>(typeof value === 'number' ? value : 0);

  React.useEffect(() => {
    if (!editing) setDraft(typeof value === 'number' ? value : 0);
  }, [value, editing]);

  const clamp = (v: number) => {
    if (unit === '%') return Math.min(100, Math.max(0, v));
    return Math.max(0, v);
  };

  const { p25, p50, p75 } = percentiles;
  const havePercentiles = p25 != null && p50 != null && p75 != null;
  const base = typeof value === 'number' && value > 0 ? value : p50 ?? 1;
  const sliderMin = havePercentiles
    ? Math.max(0, Math.round((p25 as number) * 0.9))
    : Math.max(0, Math.round(base * 0.6));
  const sliderMax = havePercentiles
    ? Math.round((p75 as number) * 1.1)
    : Math.round(base * 1.6);
  const sliderStep = unit === '%' ? 1 : Math.max(1, Math.round(base / 100));

  const sliderMarks = havePercentiles
    ? [
        { value: p25 as number, label: '25th' },
        { value: p50 as number, label: '50th' },
        { value: p75 as number, label: '75th' },
      ]
    : undefined;

  return (
    <Box
      sx={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: { xs: '0.65rem 0.5rem', sm: '1.1rem 1.15rem' },
        position: 'relative',
        overflow: 'hidden',
        minWidth: 0,
      }}
    >
      <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '3px', background: accent }} />

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '4px', mb: '0.35rem' }}>
        <Typography
          sx={{
            fontSize: { xs: '0.62rem', sm: '0.75rem' },
            fontWeight: 600,
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: { xs: '0.2px', sm: '0.5px' },
            lineHeight: 1.2,
          }}
        >
          {label}
        </Typography>
        {canEdit && !editing && (
          <IconButton
            size="small"
            onClick={() => setEditing(true)}
            aria-label={`Edit ${label}`}
            sx={{
              padding: '2px',
              color: '#6b7280',
              '&:hover': { color: accent, background: 'rgba(0,0,0,0.04)' },
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: { xs: '14px', sm: '16px' } }} />
          </IconButton>
        )}
      </Box>

      {!editing ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '6px' }}>
            <Typography sx={{ fontSize: { xs: '1.05rem', sm: '1.5rem' }, fontWeight: 800, color: '#111827', lineHeight: 1.1, wordBreak: 'break-word' }}>
              {displayValue}
            </Typography>
            {isOverridden && (
              <Box
                component="span"
                sx={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  color: accent,
                  border: `1px solid ${accent}`,
                  borderRadius: '6px',
                  padding: '1px 6px',
                  lineHeight: 1.2,
                }}
              >
                Custom
              </Box>
            )}
          </Box>
          {marketText && (
            <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' }, color: '#9ca3af', mt: '0.25rem', lineHeight: 1.2 }}>
              vs {marketText} market
            </Typography>
          )}
          {isOverridden && (
            <Button
              size="small"
              variant="text"
              onClick={onReset}
              startIcon={<RestartAltIcon sx={{ fontSize: '14px' }} />}
              sx={{
                mt: '0.35rem',
                textTransform: 'none',
                fontSize: { xs: '0.65rem', sm: '0.72rem' },
                fontWeight: 600,
                color: '#4b5563',
                padding: '0',
                minWidth: 'auto',
                '&:hover': { background: 'transparent', color: accent },
              }}
            >
              Reset
            </Button>
          )}
        </>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <TextField
              size="small"
              type="number"
              value={draft}
              onChange={e => {
                const n = Number(e.target.value);
                if (Number.isNaN(n)) return;
                setDraft(clamp(n));
              }}
              inputProps={{
                min: 0,
                max: unit === '%' ? 100 : undefined,
                step: sliderStep,
                'aria-label': `${label} value`,
                style: { padding: '6px 8px', fontSize: '0.95rem', fontWeight: 700 },
              }}
              InputProps={{
                startAdornment:
                  unit === '$' ? (
                    <Typography sx={{ fontSize: '0.85rem', color: '#6b7280', mr: '4px' }}>$</Typography>
                  ) : undefined,
                endAdornment:
                  unit === '%' ? (
                    <Typography sx={{ fontSize: '0.85rem', color: '#6b7280', ml: '4px' }}>%</Typography>
                  ) : undefined,
              }}
              sx={{ flex: 1, minWidth: 0 }}
            />
            <IconButton
              size="small"
              onClick={() => setEditing(false)}
              aria-label="Cancel"
              sx={{ padding: '4px', color: '#6b7280' }}
            >
              <CloseIcon sx={{ fontSize: '16px' }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => {
                onApply(clamp(draft));
                setEditing(false);
              }}
              aria-label="Apply"
              sx={{
                padding: '4px',
                color: '#fff',
                background: accent,
                '&:hover': { background: accent, opacity: 0.9 },
              }}
            >
              <DoneRoundedIcon sx={{ fontSize: '16px' }} />
            </IconButton>
          </Box>
          <Slider
            size="small"
            value={Math.min(Math.max(draft, sliderMin), sliderMax)}
            min={sliderMin}
            max={sliderMax}
            step={sliderStep}
            marks={sliderMarks}
            onChange={(_, v) => setDraft(clamp(v as number))}
            valueLabelDisplay="auto"
            sx={{
              color: accent,
              '& .MuiSlider-markLabel': { fontSize: '0.65rem', color: '#6b7280' },
            }}
          />
        </Box>
      )}
    </Box>
  );
}

/** Matches CalculatorGraphSection / Recharts bar tooltip: white card, title, divider, metric line. */
function SeasonalityBarTooltipContent({
  monthLabel,
  formattedAmount,
}: {
  monthLabel: string;
  formattedAmount: string;
}) {
  return (
    <Card
      elevation={0}
      sx={{
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
        maxWidth: 320,
      }}
    >
      <CardContent sx={{ padding: '10px !important', '&:last-child': { paddingBottom: '10px !important' } }}>
        <Typography variant="body2" sx={{ fontWeight: 600, marginBottom: '10px', fontSize: '0.875rem' }}>
          Estimated monthly revenue (last 12 months)
        </Typography>
        <Divider sx={{ borderColor: '#ccc' }} />
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, marginTop: '10px', fontSize: '0.875rem', color: COLOR_PRIMARY }}
        >
          {monthLabel}: {formattedAmount}
        </Typography>
      </CardContent>
    </Card>
  );
}

const ReportPageMapSectionLazy = React.lazy(() => import('./Components/ReportPageMapSection'));

const reportPageMapSectionInlineFallback = (
  <Box sx={{ minHeight: 400, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <CircularProgress sx={{ color: '#037A90' }} />
  </Box>
);


/**
 * UI-only default tab from signup persona (does not persist until user uses report-type control).
 * Only `property_manager` is special-cased. Everyone else (new/property investor, tourism, empty,
 * Trial, unclassified, unknown strings) uses the investor default: show Buy when stored type is Manage.
 * Never overrides Rent.
 */
function getPersonaOptimisticReportType(
  userDescription: string | undefined,
  stored: ReportTypes
): ReportTypes | null {
  if (stored === 'rent') return null;
  const ud = (userDescription ?? '').trim();
  if (ud === 'property_manager') {
    if (stored === 'buy') return 'manage';
    return null;
  }
  if (stored === 'manage') return 'buy';
  return null;
}

const reportPageMapSectionModalFallback = (
  <Box
    sx={{
      position: 'fixed',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0,0,0,0.25)',
      zIndex: 1300,
    }}
  >
    <CircularProgress sx={{ color: '#037A90' }} />
  </Box>
);

type ReportPageMapSectionSafeProps = React.ComponentProps<typeof ReportPageMapSectionLazy> & {
  suspenseFallback?: React.ReactNode;
};

const ReportPageMapSectionSafe = ({ suspenseFallback, ...props }: ReportPageMapSectionSafeProps) => (
  <Suspense fallback={suspenseFallback ?? reportPageMapSectionInlineFallback}>
    <ReportPageMapSectionLazy {...props} />
  </Suspense>
);


const ReportPage = ({
  reportData,
  currency,
  handleCreateNewReport,
  handleReport,
  matches,
  user,
  handleLoginOrPricingModalOpen,
  setComingSoonModalOpen,
  dummyOverviewMarket,
  loginCountryCode,
  handleOnSampleClick,
  ota = 'airbnb',
  selectedKpiPercentile,
  setSelectedKpiPercentile,
  isApiRateLimited,
}: {
  reportData: any;
  currency: string;
  handleCreateNewReport: () => void;
  handleReport: (
    method: any,
    requestBody: null | any,
    id?: string,
    storeID?: boolean,
    hideLoading?: boolean,
    forcePercentile?: KpiPercentileType,
    readSignal?: AbortSignal
  ) => Promise<{ success: boolean }>;
  matches: boolean;
  user: User;
  handleLoginOrPricingModalOpen: (
    state: 'open' | 'close',
    forceOpen?: 'payment' | null,
    estimatorSimplePricingModalViewMode?: 'expert' | 'scale',
    triggerPaymentInterestAllowed?: boolean
  ) => void;
  setComingSoonModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  dummyOverviewMarket: any;
  loginCountryCode: string;
  handleOnSampleClick: (
    reqSubscriptionPlan?: SubscriptionPlan,
    origin?: SubscriptionModalOrigin | null
  ) => void;
  ota?: 'airbnb' | 'booking';
  selectedKpiPercentile: KpiPercentileType;
  setSelectedKpiPercentile: React.Dispatch<React.SetStateAction<KpiPercentileType>>;
  isApiRateLimited: boolean;
}) => {
  const isUserReportOwner: boolean = checkIfUserAllowedToEditReport(
    user.email,
    reportData.primary_input?.created_by
  );
  const handleReportRef = useRef(handleReport);
  handleReportRef.current = handleReport;
  const [showTutorial, setShowTutorial] = useState<boolean>(false);
  const [showCalculatorWelcome, setShowCalculatorWelcome] = useState(false);
  const [realtorModalOpen, setRealtorModalOpen] = useState<boolean>(false);
  const [sidebarEditModalOpen, setSidebarEditModalOpen] = useState<boolean>(false);
  const [compsModalOpen, setCompsModalOpen] = useState<boolean>(false);
  const [filterEditModalOpen, setFilterEditModalOpen] = useState<boolean>(false);

  useEffect(() => {
    const reportPath = `https://app.airbtics.com/${reportData.primary_input.url_path}`;
    const country = getCountryFromReportData(reportData) || '';
    const bestMarketBlogURL = getBestMarketBlogURLByCountry(
      getCountryFromReportData(reportData, true) || ''
    );

    const mailchimpFields: [string, string | boolean][] = [
      [MAILCHIMP_FIELDS.LATEST_INTERESTED_REPORT_URL, reportPath],
      [MAILCHIMP_FIELDS.LATEST_INTERESTED_COUNTRY, country],
      [MAILCHIMP_FIELDS.LATEST_INTERESTED_COUNTRY_BEST_MARKET_BLOG_URL, bestMarketBlogURL],
    ];
    setUserDataToMailChimp(mailchimpFields);

    setSelectedKpiPercentile(reportData.primary_input?.last_selected_kpi || '50');
  }, []);

  const dismissCalculatorWelcome = useCallback(() => {
    if (user.email && user.email !== 'Trial') {
      try {
        localStorage.setItem(calculatorReportWelcomeStorageKey(user.email), '1');
      } catch {
        /* quota / private mode */
      }
    }
    setShowCalculatorWelcome(false);
  }, [user.email]);

  useEffect(() => {
    if (user.loading) return;
    if (!user.email || user.email === 'Trial') return;
    try {
      if (localStorage.getItem(calculatorReportWelcomeStorageKey(user.email)) === '1') return;
    } catch {
      return;
    }
    setShowCalculatorWelcome(true);
  }, [user.loading, user.email]);

  const isFirstReportUpdate = useRef(true);
  useEffect(() => {
    if (isFirstReportUpdate.current) {
      isFirstReportUpdate.current = false;
      return;
    }
    handleReport(
      'UPDATE',
      {
        update_type: 'last_selected_kpi',
        id: reportData.primary_input.id,
        last_selected_kpi: selectedKpiPercentile,
      },
      reportData.primary_input.id,
      false,
      true
    );
  }, [selectedKpiPercentile]);

  const [openedEdits, setOpenedEdits] = useState(new Set());
  const [editCounts, setEditCounts] = useState<{ [key: string]: number }>({});
  const [isOnboardingComplete, setIsOnboardingComplete] = useState(false);
  const [nearestMarket, setNearestMarket] = useState<any>(null);
  const [nearestMarketLoading, setNearestMarketLoading] = useState<boolean>(true);
  const [nearestMarketURL, setNearestMarketURL] = useState<string | null>(null);
  const [nearestMarketBasicKPIDetails, setNearestMarketBasicKPIDetails] = useState<any>(null);
  const [seasonality, setSeasonality] = useState<number | 'loading' | null>('loading');
  const [trend, setTrend] = useState<number | 'loading' | null>('loading');
  const [timePeriod, setTimePeriod] = useState(12);
  const [grapgDataArray, setGraphDataArray] = useState<any[]>([]);
  const [compsStatus, setCompsStatus] = useState<'fetching' | 'success' | 'failed'>(
    reportData.primary_input.comps_status === 'fetching' ? 'fetching' : 'success'
  );
  const [isBookingComDataRevealed, setIsBookingComDataRevealed] = useState<boolean>(false);
  const [isEditedKpiAvailable, setIsEditedKpiAvailable] = useState<boolean>(false);
  const [editedKpiKeys, setEditedKpiKeys] = useState<
    ('ltm_revenue' | 'ltm_occupancy_rate' | 'ltm_nightly_rate')[]
  >([]);

  /**
   * Ephemeral client-side overrides for the hero card's Occupancy and Nightly Rate.
   * These are NOT persisted to the backend and do NOT use the 'edited' scenario path.
   * When set, the hero recomputes Revenue locally (ADR * occupancy% * 365) and the
   * "How we estimated this revenue" callout + PercentileSelector reflect a custom state.
   */
  const [heroOverrides, setHeroOverrides] = useState<{
    ltm_occupancy_rate: number | null;
    ltm_nightly_rate: number | null;
  }>({ ltm_occupancy_rate: null, ltm_nightly_rate: null });

  const resetHeroOverrides = useCallback(() => {
    setHeroOverrides({ ltm_occupancy_rate: null, ltm_nightly_rate: null });
  }, []);

  useEffect(() => {
    resetHeroOverrides();
  }, [reportData.primary_input.id, resetHeroOverrides]);

  useEffect(() => {
    resetHeroOverrides();
  }, [selectedKpiPercentile, resetHeroOverrides]);

  const kpiData = useMemo(() => {
    const shouldShowEditedKpi = isEditedKpiAvailable && selectedKpiPercentile == 'edited';
    const percentileSlice = shouldShowEditedKpi
      ? reportData.kpi?.['edited']
      : reportData.kpi?.[selectedKpiPercentile];
    return {
      ...(percentileSlice || {}),
      ...reportData.fincal_input,
      ...reportData.fincal_input?.loan,
      ...reportData.fincal_input?.annual_operating_expenses,
      ...reportData.fincal_input?.startup_costs,
      ...reportData.fincal_input?.taxes,
      seasonality:
        seasonality == 'loading' ? seasonality : getSeasonalityBarColor(seasonality, true),
      trend,
      ...reportData.kpi,
    };
  }, [reportData, selectedKpiPercentile, seasonality, trend, isEditedKpiAvailable]);

  /**
   * Hero-card effective KPIs (server values overridden by client-side hero edits).
   * Declared early so it can be read by the `heroSummaryRevenueExplainCallout` below.
   */
  const propRevenue: number | null = kpiData?.ltm_revenue || null;
  const propOccupancy: number | null = kpiData?.ltm_occupancy_rate || null;
  const propAdr: number | null = kpiData?.ltm_nightly_rate || null;
  const effectiveOccupancy: number | null =
    heroOverrides.ltm_occupancy_rate != null ? heroOverrides.ltm_occupancy_rate : propOccupancy;
  const effectiveADR: number | null =
    heroOverrides.ltm_nightly_rate != null ? heroOverrides.ltm_nightly_rate : propAdr;
  const isHeroCustom =
    heroOverrides.ltm_occupancy_rate != null || heroOverrides.ltm_nightly_rate != null;
  const effectiveRevenue: number | null =
    isHeroCustom && effectiveOccupancy != null && effectiveADR != null
      ? Math.round(effectiveADR * (effectiveOccupancy / 100) * 365)
      : propRevenue;

  const pdfRef = useRef(null);
  const performanceIndicatorRef = useRef<any>(null);
  const [isPdfDownloadTriggerd, setIsPdfDownloadTriggerd] = useState<boolean>(false);
  const [pdfKpiSettings, setPdfKpiSettings] = useState<{
    occupancy_rate: boolean;
    adr: boolean;
    revenue: boolean;
    seasonality: boolean;
    trend: boolean;
    seasonality_svg: boolean;
    managed_by: boolean;
    nearest_market: boolean;
    forecast_income: boolean;
  } | null>(null);

  const [copyLinkModalOpen, setCopyLinkModalOpen] = useState(false);
  const [copyModalContent, setCopyModalContent] = useState<{ title: string; copyValue?: string } | null>(null);
  const [pdfDownloadModalOpen, setPdfDownloadModalOpen] = useState(false);
  const [excelExportModalOpen, setExcelExportModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingError, setIsDownloadingError] = useState(false);
  const [downloadablePdfUrl, setDownloadablePdfUrl] = useState<string | null>(null);

  const history = useHistory();

  const downloadPdf = async (settings: {
    logo?: string;
    propertyImage?: string;
    createdBy: string;
    seasonality: string | null;
    trend: number | null;
    percentile: string;
    reportPath: ReportTypes;
  }) => {
    setIsDownloading(true);
    setIsPdfDownloadTriggerd(true);
    setIsDownloadingError(false);

    const currencySymbol = exchangeRates[currency]?.symbol || '$';

    const requestBody: any = {
      report_id: reportData.primary_input.id,
      percentile: settings.percentile,
      created_by: settings.createdBy,
      currency,
      logo_url: user.organization_logo_url || null,
      currency_symbol: currencySymbol,
      template_version: 'v3',
      report_path: settings.reportPath,
    };

    if (settings.seasonality) requestBody.seasonality = settings.seasonality;
    if (settings.trend) requestBody.trend = settings.trend;
    if (settings.logo) requestBody.logo_b64 = settings.logo;
    if (settings.propertyImage) requestBody.property_image_b64 = settings.propertyImage;
    if (grapgDataArray.length > 0) requestBody.seasonality_graph_data = grapgDataArray;

    if (bookingComData) requestBody.booking_com_data = bookingComData;
    if (ltrData?.data) requestBody.ltr_data = processLtrData({ ...ltrData }, currency);

    const perfRef = performanceIndicatorRef?.current;
    const effKpi = perfRef?.effectiveKpiData;
    if (effKpi) {
      requestBody.forecast_data = {
        kpi: {
          ltm_revenue: effKpi.ltm_revenue ?? null,
          annual_operating_expenses_total: effKpi.annual_operating_expenses_total ?? null,
          net_operating_income: effKpi.net_operating_income ?? null,
          cap_rate: effKpi.cap_rate ?? null,
          cash_on_cash_return: effKpi.cash_on_cash_return ?? null,
          yearly_net_profit_before_tax: effKpi.yearly_net_profit_before_tax ?? null,
          mortgage_annual: effKpi.mortgage_annual ?? null,
          total_cash_invested: effKpi.total_cash_invested ?? null,
          annual_return_rate: effKpi.annual_return_rate ?? null,
          management_fee_dollar: effKpi.management_fee_dollar ?? null,
          opex_excluding_mgmt_fee: effKpi.opex_excluding_mgmt_fee ?? null,
          owner_payout: effKpi.owner_payout ?? null,
          monthly_rent: effKpi.monthly_rent ?? null,
        },
        inputs: {
          property_price: inputEditState?.property_price ?? null,
          monthly_rent: inputEditState?.monthly_rent ?? null,
          management_fee: inputEditState?.management_fee ?? null,
          down_payment: inputEditState?.down_payment ?? null,
          property_appreciation_rate: inputEditState?.property_appreciation_rate ?? 4,
          owner_occupied_days: inputEditState?.owner_occupied_days ?? 0,
        },
        other_metrics: [],
      };
    }

    if (settings.reportPath === 'buy') {
      const performanceIndicatorSection = document.getElementById('performance-indicators-section') as any;
      if (performanceIndicatorSection) {
        const futureProjectionGraph = performanceIndicatorSection.querySelector('svg.recharts-surface');
        if (futureProjectionGraph) requestBody.future_projection_graph = futureProjectionGraph.outerHTML;
      }
    }

    /** Match hero card: ephemeral overrides → same numbers in PDF metrics (generateCalculatorReportPDF). */
    if (
      isHeroCustom &&
      effectiveOccupancy != null &&
      effectiveADR != null &&
      effectiveRevenue != null
    ) {
      requestBody.hero_kpi_override = {
        ltm_occupancy_rate: Number(effectiveOccupancy),
        ltm_nightly_rate: Number(effectiveADR),
        ltm_revenue: Number(effectiveRevenue),
      };
    }

    try {
      const pdfReportGenereationApi =
        'https://w9f4rz5nx7.execute-api.us-east-2.amazonaws.com/default/generateCalculatorReportPDF';
      const response = await callApi(pdfReportGenereationApi, { body: requestBody }, true, false, '', 'POST', true);
      if (response.message && response.message == 'Report generated successfully') {
        const pdfUrl = response.pdf_url;
        window.open(pdfUrl, '_blank');
        setDownloadablePdfUrl(pdfUrl);
      } else {
        console.error('PDF generation failed. Response:', response);
        setIsDownloadingError(true);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      setIsDownloadingError(true);
    } finally {
      setIsPdfDownloadTriggerd(false);
      setIsDownloading(false);
    }
  };

  const handleExcelExport = (selectedPath: ReportTypes) => {
    if (reportData && kpiData && inputEditState) {
      exportReportToExcel({
        reportData,
        kpiData,
        inputEditState,
        currency,
        userName: user.userName || '',
        userEmail: user.email || '',
        reportPath: selectedPath,
      });
      logAmplitudeEvent('report_excel_exported' as any, { source: 'report_page', report_path: selectedPath });
    }
    setExcelExportModalOpen(false);
  };

  const handleShare = () => {
    setCopyModalContent({ title: 'Share your Report' });
    setCopyLinkModalOpen(true);
    logAmplitudeEvent('report_shared', { source: 'report_page' });
  };

  const handleApiCopy = () => {
    const apiUrl = `https://crap0y5bx5.execute-api.us-east-2.amazonaws.com/prod/report?id=${reportData.primary_input.id}`;
    const curlCommand = `curl --location '${apiUrl}'`;
    setCopyModalContent({ title: 'Copy API request', copyValue: curlCommand });
    setCopyLinkModalOpen(true);
  };

  const handleDownloadClick = () => {
    if (isUserSubscribed) {
      setPdfDownloadModalOpen(true);
    } else {
      handleLoginOrPricingModalOpen('open', null);
    }
  };

  // Optimistic report type for instant UI updates
  const [optimisticReportType, setOptimisticReportType] = useState<ReportTypes | null>(null);
  const personaReportEffectKeyRef = useRef('');
  const reportType: ReportTypes = optimisticReportType || reportData.primary_input.report_type;

  const reportInputsWithEffectiveType = useMemo(
    () => ({ ...reportData.primary_input, report_type: reportType }),
    [reportData.primary_input, reportType]
  );

  const [metricsState, setMetricsState] = useState({
    marketMetrics: initializeMetricsState(marketMetrics.marketData, reportType),
    incomeMetrics: initializeMetricsState(marketMetrics.income, reportType),
    expensesMetrics: initializeMetricsState(financialInputs.expenses, reportType),
    initialInvestmentMetrics: initializeMetricsState(financialInputs.initialInvestment, reportType),
    cashflowMetrics: initializeMetricsState(performanceIndicators.cashflow, reportType),
    investmentMetrics: initializeMetricsState(performanceIndicators.investmentMetricts, reportType),
  });

  const [inputEditState, setInputEditState] = useState(initializeInputEditState(kpiData));

  const [propertyNoteReportName, setPropertyNoteReportName] = useState('');
  const [propertyNoteYearBuilt, setPropertyNoteYearBuilt] = useState('');
  const [propertyNoteSizeSqm, setPropertyNoteSizeSqm] = useState('');
  const [propertyNoteLink, setPropertyNoteLink] = useState('');
  const [propertyNoteNotes, setPropertyNoteNotes] = useState('');
  const [propertyNoteSaving, setPropertyNoteSaving] = useState(false);
  const [propertyNoteImageB64, setPropertyNoteImageB64] = useState<string | null>(null);
  const [propertyNoteImageErrorKey, setPropertyNoteImageErrorKey] = useState(0);
  const [propertyNoteImageError, setPropertyNoteImageError] = useState<string | null>(null);

  const [bookingComData, setBookingComData] = useState<any>(null);
  const [bookingComDataStatus, setBookingComDataStatus] = useState<
    'fetching' | 'success' | 'failed' | null
  >(null);
  const [ltrData, setLTRData] = useState<{
    data: any;
    status: 'fetching' | 'success' | 'failed' | null;
  }>({
    data: null,
    status: null,
  });
  const [isLTRDataRevealed, setIsLTRDataRevealed] = useState<boolean>(false);
  const [heroRevenueExplainOpen, setHeroRevenueExplainOpen] = useState(false);

  const [selectedCompsCount, setSelectedCompsCount] = useState<number>(0);
  useEffect(() => {
    setSelectedCompsCount(
      reportData.comps.reduce((acc: number, comp: any) => {
        if (comp['is_selected']) return acc + 1;
        return acc;
      }, 0)
    );
  }, [reportData.comps]);

  useEffect(() => {
    const pi = reportData.primary_input;
    setPropertyNoteReportName(pi.report_name ? String(pi.report_name) : '');
    setPropertyNoteYearBuilt(pi.year_built != null ? String(pi.year_built) : '');
    setPropertyNoteSizeSqm(pi.property_size_sqm != null ? String(pi.property_size_sqm) : '');
    setPropertyNoteLink(pi.link_to_property ? String(pi.link_to_property) : '');
    setPropertyNoteNotes(pi.notes ? String(pi.notes) : '');
  }, [
    reportData.primary_input.report_name,
    reportData.primary_input.year_built,
    reportData.primary_input.property_size_sqm,
    reportData.primary_input.link_to_property,
    reportData.primary_input.notes,
  ]);

  useEffect(() => {
    setPropertyNoteImageB64(null);
  }, [reportData.primary_input.id, reportData.primary_input.property_image_urls]);

  const handlePropertyNoteImageFiles = useCallback(async (files: FileList | null) => {
    if (!files?.length) return;
    try {
      const data = (await handleImageInput(files)) as string;
      setPropertyNoteImageB64(data);
      setPropertyNoteImageError(null);
    } catch (err) {
      setPropertyNoteImageErrorKey(k => k + 1);
      setPropertyNoteImageError(typeof err === 'string' ? err : 'Could not process image');
    }
  }, []);

  const toggleVisibility = (
    category: keyof typeof metricsState,
    metricKey: string,
    show: boolean
  ) => {
    setMetricsState(prevState => {
      const updatedState = { ...prevState };
      updatedState[category][metricKey].visibility = show;
      return updatedState;
    });

    const visibilityConfig = Object.keys(metricsState).reduce((acc, category) => {
      Object.keys(metricsState[category]).forEach(metric => {
        acc[metric] = metricsState[category][metric].visibility;
      });
      return acc;
    }, {});
    localStorage.setItem('kpi_visibility', JSON.stringify(visibilityConfig));
  };

  const handleInputEdit = (key: string, value: number | boolean) => {
    setInputEditState(prevState => ({
      ...prevState,
      [key]: value,
    }));
  };

  const hanldeOnInputApply = () => {
    trackEditPerformed('calculator');
    const calculatorMutableFieldsJsonObj = {
      update_type: 'calculator',
      id: reportData.primary_input.id,
      report_type: reportType,
      ...inputEditState,
      is_loan_taken: inputEditState.is_loan_taken ? 1 : 0,
      wanna_sell: inputEditState.wanna_sell ? 1 : 0,
    };
    handleReport('UPDATE', calculatorMutableFieldsJsonObj);
  };

  const hanldeKpiEdit = (key, value) => {
    trackEditPerformed('calculator');
    const calculatorMutableFieldsJsonObj = {
      update_type: 'kpi',
      id: reportData.primary_input.id,
      report_type: reportType,
      [key]: value,
    };
    handleReport(
      'UPDATE',
      calculatorMutableFieldsJsonObj,
      reportData.primary_input.id,
      false,
      false,
      'edited'
    );
  };

  useEffect(() => {
    const { isEditedKpiAvailable, editedKpiKeys } = getIsEditedKpiAvailable(reportData);
    if (isEditedKpiAvailable) {
      setIsEditedKpiAvailable(true);
      setEditedKpiKeys(editedKpiKeys);
    }
  }, [reportData]);

  useEffect(() => {
    setCompsStatus(reportData.primary_input.comps_status);
  }, [reportData.primary_input.comps_status]);

  useEffect(() => {
    if (compsStatus !== 'fetching') return;

    const reportIdForPoll = reportData.primary_input.id;
    let attempt = 0;
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let pollAbort: AbortController | null = null;
    let delayMs = COMPS_POLL_INITIAL_MS;

    const doRead = async () => {
      if (cancelled) return;
      if (attempt >= COMPS_POLL_MAX_ATTEMPTS) {
        setCompsStatus('failed');
        return;
      }
      attempt += 1;
      pollAbort?.abort();
      pollAbort = new AbortController();
      await handleReportRef.current(
        'READ',
        null,
        reportIdForPoll,
        false,
        true,
        undefined,
        pollAbort.signal
      );
      if (cancelled) return;
      if (attempt >= COMPS_POLL_MAX_ATTEMPTS) {
        setCompsStatus('failed');
        return;
      }
      delayMs = Math.min(
        Math.round(delayMs * COMPS_POLL_BACKOFF_MULTIPLIER),
        COMPS_POLL_MAX_DELAY_MS
      );
      timeoutId = setTimeout(() => {
        void doRead();
      }, delayMs);
    };

    timeoutId = setTimeout(() => {
      void doRead();
    }, delayMs);

    return () => {
      cancelled = true;
      if (timeoutId !== null) clearTimeout(timeoutId);
      pollAbort?.abort();
    };
  }, [compsStatus, reportData.primary_input.id]);

  useEffect(() => {
    setInputEditState(initializeInputEditState(kpiData));
  }, [kpiData]);

  // Clear optimistic state when real data catches up
  useEffect(() => {
    if (optimisticReportType && optimisticReportType === reportData.primary_input.report_type) {
      setOptimisticReportType(null);
    }
  }, [reportData.primary_input.report_type, optimisticReportType]);

  useEffect(() => {
    if (user.loading) return;
    const id = reportData.primary_input.id;
    const stored = reportData.primary_input.report_type as ReportTypes;
    const ud = user.userDescription ?? '';
    const key = `${id}|${ud}|${stored}`;
    if (personaReportEffectKeyRef.current === key) return;
    personaReportEffectKeyRef.current = key;
    const next = getPersonaOptimisticReportType(ud, stored);
    if (next !== null) {
      setOptimisticReportType(next);
    }
  }, [
    reportData.primary_input.id,
    reportData.primary_input.report_type,
    user.userDescription,
    user.loading,
  ]);

  const getNearestMarket = async reportID => {
    const url =
      'https://a1rd9gu3ea.execute-api.us-east-2.amazonaws.com/default/getNearestReportMarket';
    try {
      setNearestMarketLoading(true);
      const response = await callApi(
        url,
        { queryParams: { id: reportID, bedrooms: reportData.primary_input.bedrooms } },
        false
      );
      if (response.message) {
        setNearestMarket(response.message);
        const data = response.message;
        const district = data['district'];
        const city = data?.city || '0';
        const country = data?.country ? data?.country.replace(' ', '-') : '0';
        const nearestMarketURL = `${window.location.origin}/airbnb-data/${country}/${city}/${district}`.toLowerCase();
        setNearestMarketURL(nearestMarketURL);
        setNearestMarketBasicKPIDetails({
          id: data?.id,
          name: data['display_name'] || district,
          countryCode: data?.country_code,
          occupancyRate: data?.occupancy_rate,
          adr: data?.adr,
          supply: data?.number_of_listings,
          licensePercentage: data?.license_percentage ?? data?.cached_data?.supply_breakdown?.license_percentage,
          marketGrade: data?.market_grade,
          parentMarketID: data?.parent_market_id,
          isSubmarket: data?.is_submarket,
          parentMarketName: data?.parent_market_name,
          parentMarketURL: data?.parent_market_link,
          isVerifiedMarket: data?.is_verified_market ?? false,
          seasonality: data?.seasonality ?? null,
          trend: data?.trend ?? null,
          regulation: data?.regulation ?? null,
          bedroomSpecific: data?.bedroom_specific ?? null,
        });
      }
      setNearestMarketLoading(false);
    } catch (e) {
      console.error('Failed to fetch nearest market: ', e);
      setNearestMarket(null);
      setNearestMarketLoading(false);
    }
  };

  useEffect(() => {
    const reportID = reportData.primary_input.id;
    getNearestMarket(reportID);
  }, [reportData.primary_input.id]);

  useEffect(() => {
    getLTRCompData(reportData.primary_input.id, currency, setLTRData);
  }, [reportData.primary_input.id]);

  useEffect(() => {
    // Use pre-computed seasonality/trend from backend if available
    if (nearestMarketBasicKPIDetails?.seasonality != null) {
      setSeasonality(nearestMarketBasicKPIDetails.seasonality);
    } else if (
      nearestMarket?.cached_data?.historical_monthly_performance
    ) {
      // DEPRECATED: fallback to client-side computation for backward compatibility
      try {
        const cachedMarketData = nearestMarket.cached_data;
        const parsedCachedMarketData = parseMessageHistoricalData(cachedMarketData);
        const [revenueHistoricalData] = getHistoricalData(
          parsedCachedMarketData.historical_monthly_performance,
          'revenue'
        );
        const seasonilityData = getSeasonality(
          revenueHistoricalData,
          'overview',
          selectedKpiPercentile === 'comps' || selectedKpiPercentile === 'edited'
            ? '50'
            : selectedKpiPercentile
        );
        setSeasonality(seasonilityData.seasonality);
      } catch (e) {
        console.error('Failed to compute seasonality client-side', e);
        setSeasonality(null);
      }
    } else {
      setSeasonality(null);
    }

    if (nearestMarketBasicKPIDetails?.trend != null) {
      setTrend(nearestMarketBasicKPIDetails.trend);
    } else if (
      nearestMarket?.cached_data?.historical_monthly_performance
    ) {
      // DEPRECATED: fallback to client-side computation for backward compatibility
      try {
        const cachedMarketData = nearestMarket.cached_data;
        const parsedCachedMarketData = parseMessageHistoricalData(cachedMarketData);
        const [revenueHistoricalData] = getHistoricalData(
          parsedCachedMarketData.historical_monthly_performance,
          'revenue'
        );
        const { nextYearPredcition } = calculateHistoricalTrends(
          revenueHistoricalData,
          'revenue',
          selectedKpiPercentile === 'comps' || selectedKpiPercentile === 'edited'
            ? '50'
            : selectedKpiPercentile
        );
        setTrend(nextYearPredcition);
      } catch (e) {
        console.error('Failed to compute trend client-side', e);
        setTrend(null);
      }
    } else {
      setTrend(null);
    }
    return;
  }, [
    nearestMarket,
    nearestMarketBasicKPIDetails,
    selectedKpiPercentile,
    reportData,
  ]);

  useEffect(() => {
    if (seasonality === 'loading' || trend === 'loading') {
      const maxLoadingTimeout = setTimeout(() => {
        if (seasonality === 'loading') setSeasonality(null);
        if (trend === 'loading') setTrend(null);
      }, 30000);

      return () => clearTimeout(maxLoadingTimeout);
    }
    return;
  }, [seasonality, trend]);

  const isUserSubscribed: boolean = user.subscriptionPlan != 'starter';

  const reportAccessType: UserReportAccessTypes = reportData.primary_input?.user_report_access_type;

  useEffect(() => {
    const savedOnboardingState = localStorage.getItem('user-onboarding-state');
    if (savedOnboardingState) {
      const savedOnboardingStateJSON = JSON.parse(savedOnboardingState);
      setOpenedEdits(new Set(savedOnboardingStateJSON.openedEdits));
      setEditCounts(savedOnboardingStateJSON.editCounts);
      checkOnboardingCompletion();
    }
  }, []);

  useEffect(() => {
    // persisting state across reloads
    const editCountSum = Object.values(editCounts).reduce((acc, value) => acc + value, 0);
    if (openedEdits.size == 0 && editCountSum == 0) {
      return;
    }
    localStorage.setItem(
      'user-onboarding-state',
      JSON.stringify({
        openedEdits: Array.from(openedEdits),
        editCounts,
      })
    );
    checkOnboardingCompletion();
  }, [openedEdits, editCounts]);

  useEffect(() => {
    if (isOnboardingComplete && !isUserSubscribed) {
      const timer = setTimeout(() => {
        handleLoginOrPricingModalOpen('open', 'payment', 'expert', false);
      }, 3000);
      return () => clearTimeout(timer);
    }
    return;
  }, [isOnboardingComplete]);

  useEffect(() => {
    setGraphDataArray(getSessionilityGraphData());
  }, [nearestMarket]);

  const ltmDateRange = getLtmDates(kpiData);

  const getSessionilityGraphData = () => {
    const historical = nearestMarket?.cached_data?.historical_monthly_performance;
    if (!historical) return [];

    const monthsOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const sortedDates = Object.keys(historical).sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
    const last12Dates = sortedDates.slice(0, 12);

    const byMonthIndex: Record<number, number[]> = {};
    for (let i = 1; i <= 12; i++) {
      byMonthIndex[i] = [];
    }

    last12Dates.forEach(date => {
      const [, month] = date.split('-').map(Number);
      const rev = historical[date]?.revenue_50 ?? historical[date]?.revenue50;
      if (rev != null && rev > 0) {
        byMonthIndex[month].push(Number(rev));
      }
    });

    const avg = (arr: number[]) =>
      arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;

    return monthsOrder.map((timePeriod, i) => ({
      timePeriod,
      date: `${i + 1}`.padStart(2, '0'),
      Revenue: avg(byMonthIndex[i + 1]),
    }));
  };

  if (!reportData) {
    return <Typography variant="h4">Loading...</Typography>;
  }

  const getCurrentPropertyOrRentPrice = () => {
    return reportData.fincal_input.property_price
      ? reportData.fincal_input.property_price
      : reportData.fincal_input.monthly_rent
      ? reportData.fincal_input.monthly_rent
      : null;
  };

  const checkOnboardingCompletion = () => {
    const hasOpenedTwoEdits = openedEdits.size >= 2;
    const hasEditedTwice = Object.values(editCounts).reduce((acc, count) => acc + count, 0) >= 2;

    if (hasOpenedTwoEdits || hasEditedTwice) {
      setIsOnboardingComplete(true);
    } else {
      setIsOnboardingComplete(false);
    }
  };

  const trackEditOpened = editType => {
    setOpenedEdits(prevOpenedEdits => {
      const newOpenedEdits = new Set(prevOpenedEdits);
      newOpenedEdits.add(editType);
      return newOpenedEdits;
    });

    if (!editCounts[editType]) {
      setEditCounts(prevEditCounts => ({
        ...prevEditCounts,
        [editType]: 0,
      }));
    }
  };

  const trackEditPerformed = editType => {
    setEditCounts(prevEditCounts => ({
      ...prevEditCounts,
      [editType]: (prevEditCounts[editType] || 0) + 1,
    }));
  };

  const propertyNoteDirty = useMemo(() => {
    const pi = reportData.primary_input;
    return (
      propertyNoteImageB64 != null ||
      propertyNoteReportName.trim() !== (pi.report_name || '').trim() ||
      propertyNoteYearBuilt.trim() !== (pi.year_built != null ? String(pi.year_built) : '') ||
      propertyNoteSizeSqm.trim() !== (pi.property_size_sqm != null ? String(pi.property_size_sqm) : '') ||
      propertyNoteLink.trim() !== (pi.link_to_property || '').trim() ||
      propertyNoteNotes.trim() !== (pi.notes || '').trim()
    );
  }, [
    propertyNoteImageB64,
    propertyNoteReportName,
    propertyNoteYearBuilt,
    propertyNoteSizeSqm,
    propertyNoteLink,
    propertyNoteNotes,
    reportData.primary_input.report_name,
    reportData.primary_input.year_built,
    reportData.primary_input.property_size_sqm,
    reportData.primary_input.link_to_property,
    reportData.primary_input.notes,
  ]);

  const handlePropertyNoteSave = useCallback(async () => {
    const pi = reportData.primary_input;
    trackEditPerformed('input');
    setPropertyNoteSaving(true);
    try {
      const rt = String(reportType).toLowerCase();
      const propertyPriceOrRent =
        rt === 'buy'
          ? inputEditState.property_price || reportData.fincal_input?.property_price || 0
          : rt === 'rent'
            ? inputEditState.monthly_rent || reportData.fincal_input?.monthly_rent || 0
            : null;

      const reportNameVal = propertyNoteReportName.trim() || null;
      const yearTrim = propertyNoteYearBuilt.trim();
      const buildYearVal = yearTrim ? parseInt(yearTrim, 10) : null;
      const sizeTrim = propertyNoteSizeSqm.trim();
      const sizeVal = sizeTrim ? parseFloat(sizeTrim) : null;
      const linkVal = propertyNoteLink.trim() || null;
      const notesVal = propertyNoteNotes.trim() || null;

      const payload = buildReportFilterUpdatePayload({
        reportId: pi.id,
        addressFull: pi.address_full,
        addressShort: pi.address_short,
        latitude: pi.latitude,
        longitude: pi.longitude,
        bedrooms: pi.bedrooms ?? 0,
        bathrooms: pi.bathrooms ?? 0,
        accommodates: pi.accommodates ?? 0,
        reportType,
        minNights: pi.min_nights ?? null,
        professionallyManaged: pi.professionally_managed ?? null,
        ratingMin: pi.rating_min ?? 3.9,
        ratingMax: pi.rating_max ?? 5,
        reviewCountMin: pi.review_count_min ?? null,
        amenities: pi.amenities?.length ? pi.amenities : null,
        propertyPriceOrRent,
        propertyDetails: {
          reportName: reportNameVal,
          propertySizeSqm:
            sizeVal != null && !Number.isNaN(sizeVal) && sizeVal > 0 ? sizeVal : null,
          buildYear:
            buildYearVal != null && !Number.isNaN(buildYearVal) && buildYearVal > 0
              ? buildYearVal
              : null,
          linkToProperty: linkVal,
          notes: notesVal,
          propertyImageB64: propertyNoteImageB64,
          existingPropertyImageUrl: pi.property_image_urls,
        },
      });

      await handleReport('UPDATE', payload);
    } finally {
      setPropertyNoteSaving(false);
    }
  }, [
    reportData.primary_input,
    reportData.fincal_input,
    reportType,
    inputEditState.property_price,
    inputEditState.monthly_rent,
    propertyNoteReportName,
    propertyNoteYearBuilt,
    propertyNoteSizeSqm,
    propertyNoteLink,
    propertyNoteNotes,
    propertyNoteImageB64,
    handleReport,
    trackEditPerformed,
  ]);

  const handleCompsUpdate = () => {
    const parsedSelectedComps = reportData.comps.reduce(
      (acc: { [key: string]: number }, comp: any) => {
        acc[comp.listingID] = comp.is_selected ? 1 : 0;
        return acc;
      },
      {}
    );

    const updateCompsObject = {
      update_type: 'comps',
      id: reportData.primary_input.id,
      comps: parsedSelectedComps,
    };

    handleReport('UPDATE', updateCompsObject, reportData.primary_input.id, false, false, 'comps');
  };

  // UI Elements

  const compsCurrencySymbol = exchangeRates[currency]?.symbol || '$';
  const selectedComps = reportData.comps.filter((c: any) => c.is_selected === 1);
  const compsAvg = (key: string) => {
    const vals = selectedComps.map((c: any) => c[key]).filter((v: any) => v != null && v > 0);
    return vals.length > 0 ? vals.reduce((a: number, b: number) => a + b, 0) / vals.length : null;
  };
  const avgRevenue = compsAvg('revenue_potential');
  const avgOccupancy = compsAvg('avg_occupancy_rate_ltm');
  const avgAdr = compsAvg('avg_booked_daily_rate_ltm');

  const neighborhoodPremium = reportData.primary_input.neighborhood_premium_factor;
  /** Up to 2 decimal places; trims trailing zeros (e.g. 1.42, 8). */
  const formatNeighborhoodPremiumPct = (v: number) => String(parseFloat(Number(v).toFixed(2)));
  const listingPremium = reportData.primary_input.listing_premium_factor;
  const neighborhoodRadius = reportData.primary_input.neighborhood_inner_radius;
  const bedroomMarketRevenue = nearestMarketBasicKPIDetails?.bedroomSpecific?.revenue ?? null;
  const marketName = nearestMarketBasicKPIDetails?.name || 'Market';
  const pi = reportData.primary_input;
  const hasPool = (pi.amenities || []).includes('pool');
  const premiumBreakdown = useMemo(
    () => computeListingPremiumBreakdown(pi.bedrooms || 0, pi.bathrooms || 0, pi.accommodates || 0, hasPool),
    [pi.bedrooms, pi.bathrooms, pi.accommodates, hasPool],
  );

  const traceabilityAvailable = bedroomMarketRevenue != null && neighborhoodPremium != null && listingPremium != null;

  const fmtUSD = (val: number | null) =>
    val != null ? `${compsCurrencySymbol}${formatCurrency(String(Math.round(convertCurrency(currency, val))))}` : '--';

  const fmtDisplay = (val: number | null) =>
    val != null ? formatDisplayCurrency(currency, val) : '--';

  const afterNeighborhood = bedroomMarketRevenue != null && neighborhoodPremium != null
    ? Math.round(bedroomMarketRevenue * (1 + neighborhoodPremium / 100))
    : null;
  const afterListing = afterNeighborhood != null && listingPremium != null
    ? Math.round(afterNeighborhood * (1 + listingPremium))
    : null;

  const median50LtmRaw = reportData.kpi?.['50']?.ltm_revenue;
  const medianExplainIsFromReport = median50LtmRaw != null && !Number.isNaN(Number(median50LtmRaw));
  const medianExplainLtmRevenue = medianExplainIsFromReport
    ? Number(median50LtmRaw)
    : afterListing;

  const revenueExplainNonMedianNoticeLabel =
    selectedKpiPercentile === '25'
      ? 'underperforming (25th percentile)'
      : selectedKpiPercentile === '75'
        ? 'top performing (75th percentile)'
        : selectedKpiPercentile === 'comps'
          ? 'reference comps average'
          : selectedKpiPercentile === 'edited'
            ? 'custom estimate'
            : 'your selected scenario';

  const estimatedRevenueScenarioTitleSuffix =
    selectedKpiPercentile === '25'
      ? '(25% Underperforming scenario)'
      : selectedKpiPercentile === '50'
        ? '(50% Median scenario)'
        : selectedKpiPercentile === '75'
          ? '(75% Top Performing scenario)'
          : selectedKpiPercentile === 'comps'
            ? '(Reference comps scenario)'
            : selectedKpiPercentile === 'edited'
              ? '(Custom scenario)'
              : '';

  const heroSummaryRevenueExplainCallout = isHeroCustom ? (
    <Box
      sx={{
        width: '100%',
        background: '#f3f4f6',
        borderLeft: '4px solid #9ca3af',
        borderRadius: '8px',
        overflow: 'hidden',
        boxSizing: 'border-box',
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
      }}
      aria-disabled="true"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', opacity: 0.8 }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#4b5563', lineHeight: 1.4 }}>
          How we estimated this revenue
        </Typography>
        <Typography sx={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.45 }}>
          Revenue was manually adjusted from your Occupancy Rate / Nightly Rate inputs. Custom values are not saved or included in exports.
        </Typography>
      </Box>
      <Button
        size="small"
        variant="text"
        onClick={resetHeroOverrides}
        startIcon={<RestartAltIcon sx={{ fontSize: '14px' }} />}
        sx={{
          textTransform: 'none',
          fontSize: '12px',
          fontWeight: 600,
          color: '#037A90',
          minWidth: 'auto',
          whiteSpace: 'nowrap',
          '&:hover': { background: 'rgba(3,122,144,0.08)' },
        }}
      >
        Reset
      </Button>
    </Box>
  ) : (
    <Box
      sx={{
        width: '100%',
        background: '#f0fdfa',
        borderLeft: '4px solid #037A90',
        borderRadius: '8px',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <Box
        onClick={() => setHeroRevenueExplainOpen(o => !o)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          padding: '12px 14px',
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { background: 'rgba(3,122,144,0.06)' },
        }}
        role="button"
        aria-expanded={heroRevenueExplainOpen}
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setHeroRevenueExplainOpen(o => !o);
          }
        }}
      >
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1f2937', lineHeight: 1.4, textAlign: 'left' }}>
          How we estimated this revenue
        </Typography>
        <ExpandMoreIcon
          sx={{
            color: '#037A90',
            transform: heroRevenueExplainOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            flexShrink: 0,
          }}
        />
      </Box>
      <Collapse in={heroRevenueExplainOpen}>
        <Box sx={{ px: '14px', pb: '14px' }}>
          {traceabilityAvailable ? (
            <Box>
              <Box sx={{ position: 'relative' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {selectedKpiPercentile !== '50' && (
                <Typography sx={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.45 }}>
                  This walkthrough uses the <strong>median (50th percentile)</strong> scenario. The headline estimate at the top reflects your current selection:{' '}
                  {revenueExplainNonMedianNoticeLabel}.
                </Typography>
              )}
              {(() => {
                const mockBase = !isUserSubscribed ? 52000 : null;
                const mockNhPct = !isUserSubscribed ? 8 : null;
                const mockLpPct = !isUserSubscribed ? 5 : null;
                const mockAfterNh = mockBase != null ? Math.round(mockBase * 1.08) : null;
                const mockAfterLp = mockAfterNh != null ? Math.round(mockAfterNh * 1.05) : null;
                const mockFinal = mockAfterLp ?? null;

                const displayBase = mockBase ?? bedroomMarketRevenue;
                const displayNhPct = mockNhPct ?? neighborhoodPremium;
                const displayAfterNh = mockAfterNh ?? afterNeighborhood;
                const displayLpPct = mockLpPct ?? (listingPremium != null ? Math.round(listingPremium * 100) : null);
                const displayAfterLp = mockAfterLp ?? afterListing;
                const displayFinal = mockFinal ?? medianExplainLtmRevenue;

                return (
                  <>
              <Box sx={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px 14px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '4px' }}>
                  <Box sx={{ width: '20px', height: '20px', borderRadius: '50%', background: '#037A90', color: '#fff', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>1</Box>
                  <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#1f2937' }}>Market Base Revenue ({pi.bedrooms}BD)</Typography>
                </Box>
                <Typography sx={{ fontSize: '22px', fontWeight: 800, color: '#037A90', ml: '26px' }}>
                  {fmtUSD(displayBase)}<Typography component="span" sx={{ fontSize: '12px', fontWeight: 400, color: '#6b7280' }}>/yr</Typography>
                </Typography>
                <Typography sx={{ fontSize: '11px', color: '#6b7280', ml: '26px', mt: '2px' }} component="div">
                  Median revenue for {pi.bedrooms}-bedroom listings in{' '}
                  <Typography component="span" sx={{ fontWeight: 700, color: '#037A90' }}>
                    {nearestMarketBasicKPIDetails
                      ? formatMarketName(nearestMarketBasicKPIDetails)
                      : marketName}
                  </Typography>
                </Typography>
              </Box>

              <Box sx={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px 14px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '4px' }}>
                  <Box sx={{ width: '20px', height: '20px', borderRadius: '50%', background: '#037A90', color: '#fff', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>2</Box>
                  <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#1f2937' }}>
                    Neighborhood Adjustment
                    <Typography component="span" sx={{ fontSize: '12px', fontWeight: 600, color: displayNhPct >= 0 ? '#059669' : '#dc2626', ml: '6px' }}>
                      {displayNhPct >= 0 ? '+' : ''}
                      {formatNeighborhoodPremiumPct(Number(displayNhPct))}%
                    </Typography>
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '11px', color: '#6b7280', ml: '26px', mb: '4px' }} component="div">
                  Your location performs {formatNeighborhoodPremiumPct(Math.abs(Number(displayNhPct)))}%{' '}
                  {displayNhPct >= 0 ? 'above' : 'below'} the market average
                  {!isUserSubscribed
                    ? ' (based on 1.2km neighborhood analysis)'
                    : neighborhoodRadius != null
                      ? ` (based on ${
                          neighborhoodRadius < 1
                            ? `${Math.round(neighborhoodRadius * 1000)}m`
                            : `${Math.round(neighborhoodRadius * 10) / 10}km`
                        } neighborhood analysis)`
                      : ''}{' '}
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '2px',
                      color: '#037A90',
                      fontWeight: 600,
                      verticalAlign: 'middle',
                    }}
                  >
                    (<NeighbourhoodMapHexIcon size={11} /> on map)
                  </Box>
                </Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151', ml: '26px' }}>
                  {fmtUSD(displayBase)} → {fmtUSD(displayAfterNh)}
                </Typography>
              </Box>

              <Box sx={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px 14px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '4px' }}>
                  <Box sx={{ width: '20px', height: '20px', borderRadius: '50%', background: '#037A90', color: '#fff', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>3</Box>
                  <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#1f2937' }}>
                    Property Features
                    <Typography component="span" sx={{ fontSize: '12px', fontWeight: 600, color: (displayLpPct ?? 0) >= 0 ? '#059669' : '#dc2626', ml: '6px' }}>
                      {(displayLpPct ?? 0) >= 0 ? '+' : ''}{displayLpPct ?? 0}%
                    </Typography>
                  </Typography>
                </Box>
                <Box component="ul" sx={{ m: 0, pl: '40px', listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  {!isUserSubscribed ? (
                    <>
                      <Box component="li" sx={{ fontSize: '11px', color: '#6b7280' }}>Bathrooms: 2 (typical for {pi.bedrooms}BD: 1) <Typography component="span" sx={{ fontSize: '11px', fontWeight: 600, color: '#059669', ml: '4px' }}>+3%</Typography></Box>
                      <Box component="li" sx={{ fontSize: '11px', color: '#6b7280' }}>Guests: 6 (typical for {pi.bedrooms}BD: 4) <Typography component="span" sx={{ fontSize: '11px', fontWeight: 600, color: '#059669', ml: '4px' }}>+2%</Typography></Box>
                      <Box component="li" sx={{ fontSize: '11px', color: '#6b7280' }}>Pool: No</Box>
                    </>
                  ) : (
                    <>
                  <Box component="li" sx={{ fontSize: '11px', color: '#6b7280' }}>
                    Bathrooms: {pi.bathrooms} (typical for {pi.bedrooms}BD: {premiumBreakdown.typical.bathrooms})
                    {premiumBreakdown.bathroomFactor > 0 && (
                      <Typography component="span" sx={{ fontSize: '11px', fontWeight: 600, color: '#059669', ml: '4px' }}>+{Math.round(premiumBreakdown.bathroomFactor * 100)}%</Typography>
                    )}
                  </Box>
                  <Box component="li" sx={{ fontSize: '11px', color: '#6b7280' }}>
                    Guests: {pi.accommodates} (typical for {pi.bedrooms}BD: {premiumBreakdown.typical.accommodates_upper_limit})
                    {premiumBreakdown.accommodatesFactor > 0 && (
                      <Typography component="span" sx={{ fontSize: '11px', fontWeight: 600, color: '#059669', ml: '4px' }}>+{Math.round(premiumBreakdown.accommodatesFactor * 100)}%</Typography>
                    )}
                  </Box>
                  <Box component="li" sx={{ fontSize: '11px', color: '#6b7280' }}>
                    Pool: {hasPool ? 'Yes' : 'No'}
                    {premiumBreakdown.poolFactor > 0 && (
                      <Typography component="span" sx={{ fontSize: '11px', fontWeight: 600, color: '#059669', ml: '4px' }}>+{Math.round(premiumBreakdown.poolFactor * 100)}%</Typography>
                    )}
                  </Box>
                    </>
                  )}
                </Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151', ml: '26px', mt: '6px' }}>
                  {fmtUSD(displayAfterNh)} → {fmtUSD(displayAfterLp)}
                </Typography>
              </Box>

              <Box sx={{
                background: 'linear-gradient(135deg, #f0fdfa 0%, #f0f9ff 100%)',
                border: '2px solid #037A90', borderRadius: '8px', padding: '12px 14px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1f2937' }}>
                  Your Estimated Revenue{estimatedRevenueScenarioTitleSuffix ? ` ${estimatedRevenueScenarioTitleSuffix}` : ''}
                </Typography>
                <Typography sx={{ fontSize: '22px', fontWeight: 800, color: '#037A90' }}>
                  {!isUserSubscribed
                    ? fmtUSD(displayFinal)
                    : medianExplainIsFromReport ? fmtDisplay(medianExplainLtmRevenue) : fmtUSD(medianExplainLtmRevenue)
                  }<Typography component="span" sx={{ fontSize: '12px', fontWeight: 400, color: '#6b7280' }}>/yr</Typography>
                </Typography>
              </Box>
                  </>
                );
              })()}
                </Box>
                {!isUserSubscribed && (
                  <DisabledBlurCard
                    subscriptionType="Expert"
                    onDisabledOptionClick={() => handleLoginOrPricingModalOpen('open')}
                    addBlur="2px"
                  />
                )}
              </Box>
              {!isUserSubscribed && (
                <Typography sx={{ fontSize: '11px', color: '#6b7280', textAlign: 'center', mt: 1, lineHeight: 1.45 }}>
                  Unlock the full step-by-step breakdown on Expert.
                </Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {selectedKpiPercentile !== '50' && (
                <Typography sx={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.45 }}>
                  The headline estimate at the top reflects your current selection ({revenueExplainNonMedianNoticeLabel}), which may differ from this explanation.
                </Typography>
              )}
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1f2937', lineHeight: 1.6 }}>
                Neighborhood coverage is limited in this area, so we estimated your revenue from comparable listings nearby instead of our full market-and-neighborhood model.
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );

  const SelectedCompsSummary = (
    <Grid container item xs={12}>
      <Box sx={{
        width: '100%', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px',
        padding: '20px',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: '12px' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1f2937', mb: '6px' }}>
              Similar listings (for context)
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.5 }}>
              Examples near your headline revenue band so you can sanity-check the market. Your default estimate comes
              from neighbourhood-level data on the map, not from averaging these listings — use the &quot;Reference
              comps&quot; scenario if you want the headline driven by your selection.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setCompsModalOpen(true)}
            sx={{
              fontSize: '12px', fontWeight: 600, textTransform: 'none', flexShrink: 0,
              borderColor: '#037A90', color: '#037A90', borderRadius: '8px', padding: '4px 12px',
              '&:hover': { background: '#f0fdfa', borderColor: '#026b7d' },
            }}
          >
            Edit on map
          </Button>
        </Box>

        {/* Comps Table */}
        <Box sx={{ mt: '20px', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr>
                {['Listing', 'Revenue Potential', 'ADR', 'Occupancy', 'Beds', 'Baths', 'Guests', 'Days Available', 'Revenue', 'Reviews', 'Type', 'Min Nights'].map(h => (
                  <th key={h} style={{
                    textAlign: 'left', padding: '8px 6px', borderBottom: '2px solid #e5e7eb',
                    background: '#f9fafb', fontWeight: 600, color: '#4b5563', fontSize: '11px',
                    whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {reportData.comps
                .filter((c: any) => c.is_selected === 1)
                .map((comp: any) => (
                  <tr key={comp.listingID} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '8px 6px', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <a
                        href={`https://www.airbnb.com/rooms/${comp.listingID}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#037A90', textDecoration: 'none', fontWeight: 500 }}
                      >
                        {comp.name || 'Unnamed'}
                      </a>
                    </td>
                    <td
                      style={{ padding: '8px 6px', fontWeight: 600, ...(!isUserSubscribed && { filter: 'blur(5px)', userSelect: 'none', cursor: 'pointer' }) }}
                      {...(!isUserSubscribed && { onClick: () => handleLoginOrPricingModalOpen('open') })}
                    >
                      {!isUserSubscribed
                        ? formatDisplayCurrency(currency, comp.revenue_potential || 48000)
                        : comp.revenue_potential ? formatDisplayCurrency(currency, comp.revenue_potential) : '--'}
                    </td>
                    <td style={{ padding: '8px 6px' }}>
                      {comp.avg_booked_daily_rate_ltm ? formatDisplayCurrency(currency, comp.avg_booked_daily_rate_ltm) : '--'}
                    </td>
                    <td
                      style={{ padding: '8px 6px', ...(!isUserSubscribed && { filter: 'blur(5px)', userSelect: 'none', cursor: 'pointer' }) }}
                      {...(!isUserSubscribed && { onClick: () => handleLoginOrPricingModalOpen('open') })}
                    >
                      {!isUserSubscribed
                        ? `${Math.round(comp.avg_occupancy_rate_ltm || 72)}%`
                        : comp.avg_occupancy_rate_ltm ? `${Math.round(comp.avg_occupancy_rate_ltm)}%` : '--'}
                    </td>
                    <td style={{ padding: '8px 6px' }}>{comp.bedrooms ?? '--'}</td>
                    <td style={{ padding: '8px 6px' }}>{comp.bathrooms ?? '--'}</td>
                    <td style={{ padding: '8px 6px' }}>{comp.accommodates ?? '--'}</td>
                    <td
                      style={{ padding: '8px 6px', ...(!isUserSubscribed && { filter: 'blur(5px)', userSelect: 'none', cursor: 'pointer' }) }}
                      {...(!isUserSubscribed && { onClick: () => handleLoginOrPricingModalOpen('open') })}
                    >
                      {!isUserSubscribed
                        ? (comp.activeDaysCount || 320)
                        : comp.activeDaysCount ?? '--'}
                    </td>
                    <td
                      style={{ padding: '8px 6px', ...(!isUserSubscribed && { filter: 'blur(5px)', userSelect: 'none', cursor: 'pointer' }) }}
                      {...(!isUserSubscribed && { onClick: () => handleLoginOrPricingModalOpen('open') })}
                    >
                      {!isUserSubscribed
                        ? formatDisplayCurrency(currency, comp.annualRevenueLTM || 42000)
                        : comp.annualRevenueLTM ? formatDisplayCurrency(currency, comp.annualRevenueLTM) : '--'}
                    </td>
                    <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>
                      {comp.reveiw_scores_rating || comp.review_scores_rating || '--'} ★ ({comp.visible_review_count || 0})
                    </td>
                    <td style={{ padding: '8px 6px', textTransform: 'capitalize' }}>{comp.room_type || '--'}</td>
                    <td style={{ padding: '8px 6px' }}>{comp.minimum_nights ?? '--'}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </Box>
      </Box>
    </Grid>
  );

  const ReportPageMapSectionElement = (
    <Grid container item xs={12} id="report-map-section">
      <ReportPageMapSectionSafe
        reportId={reportData.primary_input.id}
        lat={reportData.primary_input.latitude}
        lng={reportData.primary_input.longitude}
        radius={reportData.primary_input.radius}
        comps={reportData.comps}
        currency={currency}
        handleReport={handleReport}
        isUserReportOwner={isUserReportOwner}
        trackEditOpened={trackEditOpened}
        trackEditPerformed={trackEditPerformed}
        isOnboardingComplete={isOnboardingComplete}
        isUserSubscribed={isUserSubscribed}
        handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
        reportAccessType={reportAccessType}
        kpiData={kpiData}
        compsStatus={compsStatus}
        handleOnSampleClick={handleOnSampleClick}
        ota={ota}
        setSelectedKpiPercentile={setSelectedKpiPercentile}
        matches={matches}
        reportType={reportType}
      />
    </Grid>
  );

  const BookingComVacationRentalsMapSectionElement = ({
    bookingComData,
    bookingComDataStatus,
    isMapRevealed,
    mapDemoImage,
  }: {
    bookingComData: any;
    bookingComDataStatus: 'fetching' | 'success' | 'failed' | null;
    isMapRevealed?: boolean;
    mapDemoImage?: string;
  }) => {
    return (
      <Grid container item xs={12}>
        <ReportPageMapSectionSafe
          title="Vacation Rentals"
          reportId={reportData.primary_input.id}
          lat={
            bookingComData && bookingComDataStatus == 'success'
              ? bookingComData.primary_input?.latitude
              : dummyBookingComData.primary_input?.latitude
          }
          lng={
            bookingComData && bookingComDataStatus == 'success'
              ? bookingComData.primary_input?.longitude
              : dummyBookingComData.primary_input?.longitude
          }
          radius={
            bookingComData && bookingComDataStatus == 'success'
              ? bookingComData.primary_input?.vacation_rental_comps_radius
              : dummyBookingComData.primary_input?.radius
          }
          comps={
            bookingComData && bookingComDataStatus == 'success'
              ? bookingComData.vacation_rental_comps
              : dummyBookingComData.vacation_rental_comps
          }
          currency={currency}
          handleReport={handleReport}
          isUserReportOwner={isUserReportOwner}
          trackEditOpened={trackEditOpened}
          trackEditPerformed={trackEditPerformed}
          isOnboardingComplete={isOnboardingComplete}
          isUserSubscribed={isUserSubscribed}
          handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
          reportAccessType={reportAccessType}
          kpiData={kpiData}
          compsStatus={bookingComDataStatus || 'success'}
          handleOnSampleClick={handleOnSampleClick}
          ota={'booking'}
          disableRadius={true}
          matches={matches}
          isMapRevealed={isMapRevealed}
          mapDemoImage={mapDemoImage}
        />
      </Grid>
    );
  };

  const BookingComBoutiqueHotelsMapSectionElement = ({
    bookingComData,
    bookingComDataStatus,
    isMapRevealed,
    mapDemoImage,
  }: {
    bookingComData: any;
    bookingComDataStatus: 'fetching' | 'success' | 'failed' | null;
    isMapRevealed?: boolean;
    mapDemoImage?: string;
  }) => {
    return (
      <Grid container item xs={12}>
        <ReportPageMapSectionSafe
          title="Hotels"
          reportId={reportData.primary_input.id}
          lat={
            bookingComData && bookingComDataStatus == 'success'
              ? bookingComData.primary_input?.latitude
              : dummyBookingComData.primary_input?.latitude
          }
          lng={
            bookingComData && bookingComDataStatus == 'success'
              ? bookingComData.primary_input?.longitude
              : dummyBookingComData.primary_input?.longitude
          }
          radius={
            bookingComData && bookingComDataStatus == 'success'
              ? bookingComData.primary_input?.hotel_comps_radius
              : dummyBookingComData.primary_input?.radius
          }
          comps={
            bookingComData && bookingComDataStatus == 'success'
              ? bookingComData.hotel_comps
              : dummyBookingComData.hotel_comps
          }
          currency={currency}
          handleReport={handleReport}
          isUserReportOwner={isUserReportOwner}
          trackEditOpened={trackEditOpened}
          trackEditPerformed={trackEditPerformed}
          isOnboardingComplete={isOnboardingComplete}
          isUserSubscribed={isUserSubscribed}
          handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
          reportAccessType={reportAccessType}
          kpiData={kpiData}
          compsStatus={bookingComDataStatus || 'success'}
          handleOnSampleClick={handleOnSampleClick}
          ota={'booking'}
          disableRadius={true}
          matches={matches}
          isMapRevealed={isMapRevealed}
          mapDemoImage={mapDemoImage}
        />
      </Grid>
    );
  };

  const LTRCompMapSectionElement = ({
    ltrData,
    ltrDataStatus,
    isMapRevealed,
    mapDemoImage,
  }: {
    ltrData: any;
    ltrDataStatus: 'fetching' | 'success' | 'failed' | null;
    isMapRevealed?: boolean;
    mapDemoImage?: string;
  }) => {
    return (
      <Grid container item xs={12}>
        <ReportPageMapSectionSafe
          title="Long-term Rental Comps"
          reportId={reportData.primary_input.id}
          lat={reportData.primary_input.latitude}
          lng={reportData.primary_input.longitude}
          radius={reportData.primary_input.radius}
          comps={
            ltrData.data && ltrDataStatus == 'success' ? ltrData.data.comps : dummyLTRData.comps
          }
          currency={currency}
          handleReport={handleReport}
          isUserReportOwner={isUserReportOwner}
          trackEditOpened={trackEditOpened}
          trackEditPerformed={trackEditPerformed}
          isOnboardingComplete={isOnboardingComplete}
          isUserSubscribed={isUserSubscribed}
          handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
          reportAccessType={reportAccessType}
          kpiData={kpiData}
          compsStatus={ltrData.status || 'success'}
          handleOnSampleClick={handleOnSampleClick}
          ota={'ltr'}
          disableRadius={true}
          matches={matches}
          isMapRevealed={isMapRevealed}
          mapDemoImage={mapDemoImage}
        />
      </Grid>
    );
  };


  const InputSectionElement = (
    <Grid container item xs={12}>
      <RepotPageInputSection
        reportInputs={reportInputsWithEffectiveType}
        currentPropertyPriceOrRentPrice={getCurrentPropertyOrRentPrice()}
        handleReport={handleReport}
        currency={currency}
        isUserReportOwner={isUserReportOwner}
        trackEditOpened={trackEditOpened}
        trackEditPerformed={trackEditPerformed}
        isOnboardingComplete={isOnboardingComplete}
        isUserSubscribed={isUserSubscribed}
        handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
        loginCountryCode={loginCountryCode}
        selectedKpiPercentile={selectedKpiPercentile}
        setSelectedKpiPercentile={setSelectedKpiPercentile}
        selectedCompsCount={selectedCompsCount}
        isCompsKpiPresent={'comps' in reportData.kpi}
        handleCompsUpdate={handleCompsUpdate}
        compsStatus={compsStatus}
        isEditedKpiAvailable={isEditedKpiAvailable}
      />
    </Grid>
  );

  const MarketMetricsSection = (
    <Grid container item xs={12}>
      {kpiData && (
        <CalculatorSection
          title="Market Metrics"
          subsections={[
            {
              title: 'Market Data',
              key: 'marketMetrics',
              sourceTag: '',
              headingTooltipConfig: CALCULATOR_MARKET_DATA_TOOLTIP,
            },
            { title: 'Income', key: 'incomeMetrics' },
          ]}
          reportKPIData={kpiData}
          currency={currency}
          isUserReportOwner={isUserReportOwner}
          metrics={metricsState}
          toggleVisibility={toggleVisibility}
          hanldeOnInputApply={hanldeOnInputApply}
          hanldeKpiEdit={hanldeKpiEdit}
          trackEditOpened={trackEditOpened}
          trackEditPerformed={trackEditPerformed}
          reportType={reportType}
          inputEditState={inputEditState}
          handleInputEdit={handleInputEdit}
          selectedCompsCount={selectedCompsCount}
          handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
          isUserSubscribed={isUserSubscribed}
          ltmDateRange={ltmDateRange}
          showEditedBadge={isEditedKpiAvailable && selectedKpiPercentile == 'edited'}
          editedMetrics={editedKpiKeys}
        />
      )}
    </Grid>
  );

  const otaMetricsState = {
    marketMetrics: {
      supplyVacationRentals: {
        visibility: true,
        config: {
          displayName: 'Active Vacation Rentals',
          key: 'supply_vacation_rentals',
          unit: '',
          isEditable: false,
          editMode: null,
          allowedModes: ['buy', 'manage', 'rent'] as EditMode[],
          showAsDeafaultIn: ['buy', 'manage', 'rent'] as EditMode[],
          tooltipConfig: {
            name: 'Active Vacation Rentals',
            timePeriod: '',
            source: '',
            definition: [
              `Number of single-unit properties (e.g., vacation homes, villas, condos) available on Booking.com ${
                bookingComData
                  ? 'in ' +
                    (bookingComData?.['market']?.['display_name'] ||
                      bookingComData?.['market']?.['district'])
                  : ''
              }` + '.',
            ],
            ctaText: '',
            ctaURL: '',
          },
        },
        allowed: true,
      },
      supplyBoutiqueHotels: {
        visibility: true,
        config: {
          displayName: 'Active Boutique Hotels',
          key: 'supply_boutique_hotels',
          unit: '',
          isEditable: false,
          editMode: null,
          allowedModes: ['buy', 'manage', 'rent'] as EditMode[],
          showAsDeafaultIn: ['buy', 'manage', 'rent'] as EditMode[],
          tooltipConfig: {
            name: 'Active Boutique Hotels',
            timePeriod: '',
            source: '',
            definition: ['Hotels with 5 to 30 rooms on Booking.com.'],
            ctaText: '',
            ctaURL: '',
          },
        },
        allowed: true,
      },
      supplyLargeHotelsAndResorts: {
        visibility: true,
        config: {
          displayName: 'Active Large Hotels & Resorts',
          key: 'supply_large_hotels_and_resorts',
          unit: '',
          isEditable: false,
          editMode: null,
          allowedModes: ['buy', 'manage', 'rent'] as EditMode[],
          showAsDeafaultIn: ['buy', 'manage', 'rent'] as EditMode[],
          tooltipConfig: {
            name: 'Active Large Hotels & Resorts',
            timePeriod: '',
            source: '',
            definition: ['Hotels or resorts with 30+ rooms on Booking.com.'],
            ctaText: '',
            ctaURL: '',
          },
        },
        allowed: true,
      },
      hotelAdr: {
        visibility: true,
        config: {
          displayName: 'Hotel Nightly Rate',
          key: 'hotel_nightly_rate',
          unit: '$',
          isEditable: false,
          editMode: null,
          allowedModes: ['buy', 'manage', 'rent'] as EditMode[],
          showAsDeafaultIn: ['buy', 'manage', 'rent'] as EditMode[],
          tooltipConfig: {
            name: 'Hotel Nightly Rate',
            timePeriod: '',
            source: '',
            definition: ['Average nightly rate for hotel rooms, based on Airbnb hotel listings.'],
            ctaText: '',
            ctaURL: '',
          },
        },
        allowed: true,
      },
    },
  };

  const ltrMetricsState = {
    marketMetrics: {
      rentRange: {
        visibility: true,
        config: {
          displayName: 'Rent Range (Monthly)',
          key: 'rent_range',
          unit: '$',
          isEditable: false,
          editMode: null,
          allowedModes: ['buy', 'manage', 'rent'] as EditMode[],
          showAsDeafaultIn: ['buy', 'manage', 'rent'] as EditMode[],
          tooltipConfig: {
            name: 'Rent Range',
            timePeriod: '',
            source: '',
            definition: [
              `Rent range calculated using local market data (40th–60th percentile). Verified with Zillow.`,
            ],
            ctaText: '',
            ctaURL: '',
          },
        },
        allowed: true,
      },
    },
  };

  const handleOnBookingComDataRevealed = () => {
    if (user.subscriptionPlan === 'starter') {
      handleLoginOrPricingModalOpen('open', 'payment');
      return;
    }
    
    // Trigger the API call when user clicks to reveal Booking.com insights
    if (!bookingComData && bookingComDataStatus !== 'fetching' && bookingComDataStatus !== 'success') {
      getBookingComData(
        reportData.primary_input,
        currency,
        setBookingComData,
        setBookingComDataStatus
      );
    }
    
    setIsBookingComDataRevealed(true);
  };

  const handleOnLTRDataRevealed = () => {
    if (user.subscriptionPlan === 'starter') {
      handleLoginOrPricingModalOpen('open', 'payment');
      return;
    }
    setIsLTRDataRevealed(true);
  };


  const LTRMarketMetricsSection = (
    <Grid container item xs={12}>
      <OtaMetricsSection
        title={`Long Term Rental Insights`}
        subsections={[
          {
            title: 'Market Metrics',
            key: 'marketMetrics',
            sourceTag: '',
          },
        ]}
        reportKPIData={ltrData.data ? ltrData.data.kpi : dummyLTRData.kpi}
        currency={currency}
        isUserReportOwner={isUserReportOwner}
        metrics={ltrMetricsState}
        hanldeKpiEdit={() => {}}
        selectedCompsCount={0}
        ltmDateRange={ltmDateRange}
        MapSectionElements={[
          <LTRCompMapSectionElement
            ltrData={ltrData}
            ltrDataStatus={ltrData.status}
            isMapRevealed={isLTRDataRevealed}
            mapDemoImage={OtaComMapImage}
          />,
        ]}
        isRevealed={isLTRDataRevealed}
        setIsBookingComDataRevealed={setIsLTRDataRevealed}
        status={ltrData.status}
        handleOnBookingComDataRevealed={handleOnLTRDataRevealed}
        matches={matches}
      />
    </Grid>
  );


  const handleReportTypeChange = (newType: ReportTypes) => {
    // Client-only: persist report_type on calculator / KPI / filter / notes saves, not on chip click
    setOptimisticReportType(newType);
  };

  const performanceSubsections = [
    { title: 'Income', key: 'incomeMetrics' },
    { title: 'Expenses', key: 'expensesMetrics' },
    { title: 'Initial Investment', key: 'initialInvestmentMetrics' },
    { title: 'Cashflow', key: 'cashflowMetrics' },
    { title: 'Investment Metrics', key: 'investmentMetrics' },
  ];

  const textFieldSx = {
    '& .MuiOutlinedInput-root': { fontSize: '13px' },
    '& .MuiInputLabel-root': { fontSize: '13px' },
  } as const;

  const propertyNoteImagePreviewSrc =
    propertyNoteImageB64 ??
    (reportData.primary_input.property_image_urls
      ? encodeURI(reportData.primary_input.property_image_urls)
      : null);

  const PropertyNoteSection = (
    <Grid container item xs={12}>
      <Box sx={{
        width: '100%', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px',
        padding: '20px',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: '16px' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1f2937' }}>
            Property Note
          </Typography>
          {isUserReportOwner && (
            <Button
              variant="contained"
              size="small"
              disabled={!propertyNoteDirty || propertyNoteSaving}
              onClick={() => handlePropertyNoteSave()}
              sx={{ textTransform: 'none', fontWeight: 600, minWidth: '100px' }}
            >
              {propertyNoteSaving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: '16px', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
          {propertyNoteImagePreviewSrc ? (
            <Box
              sx={{
                width: '120px',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: '6px',
              }}
            >
              <Box
                sx={{
                  height: '90px',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  border: '1px solid #e5e7eb',
                }}
              >
                <img
                  src={propertyNoteImagePreviewSrc}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  alt=""
                />
              </Box>
              {isUserReportOwner && (
                <Button
                  component="label"
                  role={undefined}
                  variant="outlined"
                  size="small"
                  tabIndex={-1}
                  startIcon={<AddPhotoAlternateIcon sx={{ fontSize: '16px !important' }} />}
                  sx={{
                    textTransform: 'none',
                    fontSize: '11px',
                    fontWeight: 600,
                    minHeight: 0,
                    py: '4px',
                    borderColor: '#e5e7eb',
                    color: '#037A90',
                  }}
                >
                  Change photo
                  <PropertyNoteVisuallyHiddenInput
                    type="file"
                    accept="image/*"
                    onChange={e => {
                      void handlePropertyNoteImageFiles(e.target.files);
                      e.target.value = '';
                    }}
                  />
                </Button>
              )}
            </Box>
          ) : isUserReportOwner ? (
            <Button
              component="label"
              role={undefined}
              variant="outlined"
              tabIndex={-1}
              sx={{
                width: '120px',
                height: '90px',
                borderRadius: '10px',
                flexShrink: 0,
                borderStyle: 'dashed',
                borderColor: '#d1d5db',
                borderWidth: '2px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                background: '#fafafa',
                textTransform: 'none',
                '&:hover': { borderColor: '#037A90', background: '#f0fdfa' },
              }}
            >
              <AddPhotoAlternateIcon sx={{ fontSize: '22px', color: '#9ca3af', mb: '4px' }} />
              <Typography sx={{ fontSize: '11px', color: '#9ca3af', textAlign: 'center', px: 1 }}>
                Add photo
              </Typography>
              <PropertyNoteVisuallyHiddenInput
                type="file"
                accept="image/*"
                onChange={e => {
                  void handlePropertyNoteImageFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </Button>
          ) : (
            <Box sx={{
              width: '120px', height: '90px', borderRadius: '10px', flexShrink: 0,
              border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: '#fafafa',
            }}>
              <Typography sx={{ fontSize: '11px', color: '#9ca3af' }}>No photo</Typography>
            </Box>
          )}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {isUserReportOwner ? (
              <>
                <TextField
                  fullWidth
                  size="small"
                  label="Property name"
                  placeholder="Short title for this report"
                  value={propertyNoteReportName}
                  onChange={e => setPropertyNoteReportName(e.target.value)}
                  sx={{ ...textFieldSx, mb: '12px' }}
                  inputProps={{ maxLength: 500 }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px', mb: '12px' }}>
                  <TextField
                    size="small"
                    label="Year built"
                    placeholder="e.g. 1998"
                    value={propertyNoteYearBuilt}
                    onChange={e => setPropertyNoteYearBuilt(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    sx={{ ...textFieldSx, width: { xs: '100%', sm: '120px' } }}
                  />
                  <TextField
                    size="small"
                    label="Size (m²)"
                    placeholder="m²"
                    value={propertyNoteSizeSqm}
                    onChange={e => setPropertyNoteSizeSqm(e.target.value.replace(/[^\d.]/g, ''))}
                    sx={{ ...textFieldSx, width: { xs: '100%', sm: '120px' } }}
                  />
                  <TextField
                    size="small"
                    label="Listing URL"
                    placeholder="https://…"
                    value={propertyNoteLink}
                    onChange={e => setPropertyNoteLink(e.target.value)}
                    sx={{ ...textFieldSx, flex: 1, minWidth: '180px' }}
                    inputProps={{ maxLength: 1000 }}
                  />
                </Box>
                <Box sx={{
                  background: '#f9fafb', borderRadius: '8px', padding: '12px 14px',
                  borderLeft: '3px solid #037A90',
                }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Notes"
                    placeholder="Add notes for your property (visible on shared report when saved)"
                    value={propertyNoteNotes}
                    onChange={e => setPropertyNoteNotes(e.target.value)}
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiInput-root': { fontSize: '13px' },
                      '& .MuiInputLabel-root': { fontSize: '13px' },
                    }}
                    inputProps={{ maxLength: 1000 }}
                  />
                </Box>
              </>
            ) : (
              <>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1f2937', mb: '8px' }}>
                  {reportData.primary_input.report_name?.trim()
                    ? reportData.primary_input.report_name
                    : <Typography component="span" sx={{ color: '#9ca3af', fontWeight: 500 }}>—</Typography>}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', mb: '10px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <CalendarTodayIcon sx={{ fontSize: '14px', color: '#6b7280' }} />
                    <Typography sx={{ fontSize: '13px', color: '#4b5563' }}>
                      {reportData.primary_input.year_built
                        ? `Built ${reportData.primary_input.year_built}`
                        : <Typography component="span" sx={{ color: '#9ca3af' }}>Year built —</Typography>}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '13px', color: '#4b5563' }}>
                    {reportData.primary_input.property_size_sqm
                      ? `${Math.round(reportData.primary_input.property_size_sqm)} m²`
                      : <Typography component="span" sx={{ color: '#9ca3af' }}>Size —</Typography>}
                  </Typography>
                  {reportData.primary_input.link_to_property ? (
                    <Box
                      component="a"
                      href={reportData.primary_input.link_to_property}
                      target="_blank"
                      rel="noopener"
                      sx={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        fontSize: '13px', color: '#037A90', textDecoration: 'none',
                        fontWeight: 600,
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      View listing <OpenInNew sx={{ fontSize: '14px' }} />
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: '13px', color: '#9ca3af' }}>Listing link —</Typography>
                  )}
                </Box>
                <Box sx={{
                  background: '#f9fafb', borderRadius: '8px', padding: '12px 14px',
                  borderLeft: '3px solid #037A90',
                }}>
                  <Typography sx={{ fontSize: '13px', color: '#4b5563', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {reportData.primary_input.notes?.trim()
                      ? reportData.primary_input.notes
                      : <Typography component="span" sx={{ color: '#9ca3af', fontStyle: 'italic' }}>No notes yet.</Typography>}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>

        <Typography sx={{ fontSize: '11px', color: '#9ca3af', mt: '12px' }}>
          Created on {beautifyDate(reportData.primary_input.created_at)}
        </Typography>
      </Box>
    </Grid>
  );

  const PerformanceIndicatorsSection = (
    <Grid container item xs={12} id="performance-indicators-section">
      <InlineCalculatorForm
        reportType={reportType}
        onReportTypeChange={handleReportTypeChange}
        inputEditState={inputEditState}
        handleInputEdit={handleInputEdit}
        hanldeOnInputApply={hanldeOnInputApply}
        hanldeKpiEdit={hanldeKpiEdit}
        kpiData={kpiData}
        currency={currency}
        metricsState={metricsState}
        isUserReportOwner={isUserReportOwner}
        handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
        isUserSubscribed={isUserSubscribed}
        selectedCompsCount={selectedCompsCount}
        ltmDateRange={ltmDateRange}
        performanceRef={performanceIndicatorRef}
        subsections={performanceSubsections}
        toggleVisibility={toggleVisibility}
        trackEditOpened={trackEditOpened}
        reportId={reportData.primary_input.id}
      />
    </Grid>
  );

  const DemoMarketStatisticButton = () => {
    const StatsBox = (
      kpiName: string,
      value: number | string,
      color: string = COLOR_TEXT_1,
      isSeasonality: boolean = false
    ) => {
      const isMarketGrade = kpiName === 'Market Grade';
      const isGradeA = isMarketGrade && (value || '').toString().toUpperCase().startsWith('A');
      return (
        <Grid
          container
          item
          xs={12}
          justifyContent={'center'}
          alignContent={'center'}
          alignItems={'center'}
          sx={{ ...styles.paper, height: isSeasonality ? '160px' : '100px', padding: '20px' }}
        >
          <Grid item xs={12} alignItems={'center'} sx={{ marginBottom: '10px' }}>
            <Typography variant="body1" sx={{ fontSize: '16px', color: COLOR_TEXT_4 }}>
              {kpiName}{' '}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            container
            direction={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            {!isSeasonality && (
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: isGradeA ? undefined : color,
                  ...(isGradeA && getGradeAGradient()),
                }}
              >
                {value}
              </Typography>
            )}
            {isSeasonality && (
              <img
                src={DEMO_SEASONALITY_CHART}
                style={{
                  width: '80%',
                  height: '100px',
                  filter: 'blur(4px)',
                  objectFit: 'fill',
                  display: 'block',
                  margin: '0 auto',
                }}
              />
            )}
          </Grid>
        </Grid>
      );
    };

    return (
      <>
        {nearestMarketLoading && (
          <Grid
            container
            item
            xs={12}
            justifyContent="center"
            sx={{ ...(matches ? { marginBottom: '60px' } : {}), ...{ height: 'auto' } }}
          >
            <Grid
              container
              justifyContent={'flex-start'}
              alignContent={'center'}
              alignItems={'center'}
              sx={{ ...styles.paper, ...styles.reportKPISection, padding: '20px' }}
            >
              <LoadingCard type="bars" height="100px" noShadow={true} />
            </Grid>
          </Grid>
        )}
        {!nearestMarketLoading && nearestMarketBasicKPIDetails && (
          <Grid
            container
            item
            xs={12}
            justifyContent="center"
            sx={{ ...(matches ? { marginBottom: '60px' } : {}), ...{ height: 'auto' } }}
          >
            <Grid
              container
              justifyContent={'flex-start'}
              alignContent={'center'}
              alignItems={'center'}
              sx={{ ...styles.paper, ...styles.reportKPISection, padding: '20px' }}
            >
              <>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1,
                    color: COLOR_TEXT_GREY,
                    fontSize: '14px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: 500,
                  }}
                >
                  Nearest Market to Your Property
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    my: '20px',
                    fontWeight: 'bold',
                    fontSize: '24px',
                  }}
                >
                  <img
                    src={`https://flagcdn.com/w80/${nearestMarketBasicKPIDetails?.countryCode?.toLowerCase()}.png`}
                    alt="flag"
                    style={{
                      marginRight: '8px',
                      width: '30px',
                      height: '20px',
                      borderRadius: '4px',
                    }}
                  />
                  <Link
                    underline="hover"
                    href={nearestMarketURL || ''}
                    target="_blank"
                    sx={{ color: 'inherit' }}
                  >
                    {formatMarketName(nearestMarketBasicKPIDetails)}
                  </Link>
                  {nearestMarketBasicKPIDetails?.parentMarketName && (
                    <>
                      , submarket in{' '}
                      <Link
                        underline="hover"
                        href={nearestMarketBasicKPIDetails?.parentMarketURL || ''}
                        target="_blank"
                      >
                        {formatMarketName({
                          name: nearestMarketBasicKPIDetails?.parentMarketName,
                        } as Search)}
                      </Link>
                    </>
                  )}
                </Typography>
                {nearestMarketBasicKPIDetails && (
                  <Grid container item xs={12} spacing={2}>
                    <Grid item xs={6}>
                      {StatsBox(
                        'Market Grade',
                        nearestMarketBasicKPIDetails.marketGrade,
                        getGradeColor(nearestMarketBasicKPIDetails.marketGrade)
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      {StatsBox(
                        'Occupancy Rate',
                        nearestMarketBasicKPIDetails.occupancyRate != null
                          ? `${nearestMarketBasicKPIDetails.occupancyRate}%`
                          : '—'
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      {StatsBox(
                        'ADR',
                        nearestMarketBasicKPIDetails.adr != null
                          ? exchangeRates[currency].symbol +
                            formatCurrency(
                              String(convertCurrency(currency, nearestMarketBasicKPIDetails.adr))
                            )
                          : '—'
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      {StatsBox(
                        'Number of Listings',
                        nearestMarketBasicKPIDetails.supply != null
                          ? formatCurrency(String(nearestMarketBasicKPIDetails.supply))
                          : '—'
                      )}
                    </Grid>
                    {nearestMarketBasicKPIDetails.licensePercentage != null && (
                      <Grid item xs={6}>
                        {StatsBox(
                          'Regulation',
                          nearestMarketBasicKPIDetails.licensePercentage < 50 ? 'Lenient' : 'Strict',
                          nearestMarketBasicKPIDetails.licensePercentage < 50
                            ? COLOR_GREEN
                            : COLOR_RED
                        )}
                      </Grid>
                    )}
                  </Grid>
                )}
              </>

              <Grid container item xs={12} justifyContent={'center'}>
                <Button
                  variant="outlined"
                  sx={{
                    ...styles.actionSectionButton,
                    width: 'auto',
                    margin: '20px auto 0px auto',
                    px: '30px',
                  }}
                  endIcon={<OpenInNew />}
                  onClick={() => {
                    window.open(
                      nearestMarketURL ? nearestMarketURL : getDummyMarketURL(dummyOverviewMarket),
                      '_self'
                    );
                  }}
                >
                  Analyze {formatMarketName(nearestMarketBasicKPIDetails)}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        )}
      </>
    );
  };

  const Top10MarketsSection = () => (
    <Grid
      container
      item
      xs={12}
      justifyContent="center"
      sx={{ ...(matches ? { marginBottom: '60px' } : {}), ...{ height: 'auto' } }}
    >
      <Grid
        container
        justifyContent={'flex-start'}
        alignContent={'center'}
        alignItems={'center'}
        sx={{ ...styles.paper, ...styles.reportKPISection, padding: '20px' }}
      >
        <Top10Markets
          currency={currency}
          isLoggedIn={true}
          countryCode={nearestMarketBasicKPIDetails?.countryCode}
          hideBluredRows={true}
          ready={true}
        />
      </Grid>
    </Grid>
  );

  const realtorLocation =
    nearestMarketBasicKPIDetails?.parentMarketName ||
    getCountryFromReportData(reportData) ||
    '';

  const RealtorFormModal = nearestMarketBasicKPIDetails?.countryCode === 'US' ? (
    <RealtorForm
      location={realtorLocation}
      open={realtorModalOpen}
      onClose={() => setRealtorModalOpen(false)}
      onOpen={() => setRealtorModalOpen(true)}
      hideCard
    />
  ) : null;

  const streetName = (reportData.primary_input.address_full || '').split(',')[0].trim();
  const bedrooms = reportData.primary_input.bedrooms || 0;

  const heroComps = reportData.comps || [];
  const heroLat = typeof reportData.primary_input.latitude === 'number'
    ? reportData.primary_input.latitude : Number(reportData.primary_input.latitude) || 0;
  const heroLng = typeof reportData.primary_input.longitude === 'number'
    ? reportData.primary_input.longitude : Number(reportData.primary_input.longitude) || 0;

  const heroMapZoom = useMemo(
    () => computeHeroMapZoom(reportData.primary_input.neighborhood_inner_radius),
    [reportData.primary_input.neighborhood_inner_radius]
  );

  const heroMapboxStaticUrl = useMemo(() => {
    const lat = reportData.primary_input.latitude;
    const lng = reportData.primary_input.longitude;
    if (!MAPBOX_ACCESS_TOKEN || lat == null || lng == null) return null;
    const nLat = typeof lat === 'number' ? lat : Number(lat);
    const nLng = typeof lng === 'number' ? lng : Number(lng);
    if (Number.isNaN(nLat) || Number.isNaN(nLng)) return null;
    return getMapboxStaticPropertyPreviewUrl(nLat, nLng, MAPBOX_ACCESS_TOKEN, {
      zoom: heroMapZoom, width: HERO_MAP_W, height: HERO_MAP_H,
    });
  }, [reportData.primary_input.latitude, reportData.primary_input.longitude, heroMapZoom]);

  const heroNeighborhoodPolygons = useMemo(() => {
    const geom = reportData.primary_input.neighborhood_boundary as NeighborhoodGeom | null | undefined;
    if (!geom) return [] as string[];
    return neighborhoodGeometryToPolygonPointStrings(geom, heroLng, heroLat, heroMapZoom);
  }, [reportData.primary_input.neighborhood_boundary, heroLng, heroLat, heroMapZoom]);

  const marketOccupancy = nearestMarketBasicKPIDetails?.bedroomSpecific?.occupancy_rate
    ?? nearestMarketBasicKPIDetails?.occupancyRate ?? null;
  const marketAdr = nearestMarketBasicKPIDetails?.bedroomSpecific?.adr
    ?? nearestMarketBasicKPIDetails?.adr ?? null;
  const marketRevenue = nearestMarketBasicKPIDetails?.bedroomSpecific?.revenue ?? (
    marketAdr && marketOccupancy ? Math.round(marketAdr * (marketOccupancy / 100) * 365) : null
  );

  const currencySymbol = exchangeRates[currency]?.symbol || '$';

  // Shared map card
  const HeroMapCard = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: { md: '100%' },
      }}
    >
      <Box
        sx={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          overflow: 'hidden',
          position: 'relative',
          flex: { md: 1 },
          minHeight: { xs: '320px', md: '320px' },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ width: '100%', flex: 1, minHeight: { xs: '320px', md: 0 }, position: 'relative' }}>
          {heroMapboxStaticUrl ? (
            <>
              <Box
                component="img"
                src={heroMapboxStaticUrl}
                alt="Property location"
                loading="lazy"
                decoding="async"
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                {heroNeighborhoodPolygons.length > 0 && (
                  <Box
                    component="svg"
                    viewBox={`0 0 ${HERO_MAP_W} ${HERO_MAP_H}`}
                    preserveAspectRatio="none"
                    sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 0 }}
                  >
                    {heroNeighborhoodPolygons.map((points, i) => (
                      <polygon key={i} points={points} fill="rgba(3,122,144,0.15)" stroke="#037A90" strokeWidth={2} vectorEffect="non-scaling-stroke" />
                    ))}
                  </Box>
                )}
                {heroComps.map((comp: any) => {
                  if (comp.latitude == null || comp.longitude == null) return null;
                  const cLat = typeof comp.latitude === 'number' ? comp.latitude : Number(comp.latitude);
                  const cLng = typeof comp.longitude === 'number' ? comp.longitude : Number(comp.longitude);
                  if (Number.isNaN(cLat) || Number.isNaN(cLng)) return null;
                  const pos = lngLatToPixel(cLng, cLat, heroLng, heroLat, heroMapZoom, HERO_MAP_W, HERO_MAP_H);
                  const xPct = (pos.x / HERO_MAP_W) * 100;
                  const yPct = (pos.y / HERO_MAP_H) * 100;
                  if (xPct < -5 || xPct > 105 || yPct < -5 || yPct > 105) return null;
                  const isSelected = comp.is_selected == 1;
                  return (
                    <Box
                      key={comp.listingID || `${cLat}-${cLng}`}
                      sx={{
                        position: 'absolute', left: `${xPct}%`, top: `${yPct}%`,
                        width: isSelected ? '12px' : '9px', height: isSelected ? '12px' : '9px',
                        borderRadius: '50%', background: isSelected ? '#FF2079' : '#757575',
                        boxShadow: isSelected ? '0 0 0 3px rgba(255,32,121,0.3)' : '0 0 0 2px rgba(117,117,117,0.3)',
                        transform: 'translate(-50%, -50%)', zIndex: isSelected ? 2 : 1,
                      }}
                    />
                  );
                })}
                {compsStatus === 'fetching' && (
                  <Box sx={{
                    position: 'absolute', bottom: '12px', left: '12px', display: 'flex', alignItems: 'center', gap: '6px',
                    background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(4px)', borderRadius: '20px', padding: '5px 12px', zIndex: 4,
                    '@keyframes heroCompsPulse': { '0%, 100%': { opacity: 0.55 }, '50%': { opacity: 1 } },
                    animation: 'heroCompsPulse 1.8s ease-in-out infinite',
                  }}>
                    <Box sx={{ width: '6px', height: '6px', borderRadius: '50%', background: '#FF2079', boxShadow: '0 0 0 2px rgba(255,32,121,0.3)' }} />
                    <Typography sx={{ fontSize: '11px', color: '#4b5563', fontWeight: 500, lineHeight: 1 }}>Loading comparables…</Typography>
                  </Box>
                )}
                <Box sx={{ position: 'absolute', left: '50%', top: '50%', width: '24px', height: '24px', transform: 'translate(-50%, -100%)', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.28))', zIndex: 3 }}>
                  <HeroMapSubjectPin size={24} />
                </Box>
              </Box>
            </>
          ) : (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6', padding: '16px', textAlign: 'center' }}>
              <Typography sx={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.5, maxWidth: '280px' }}>
                Map preview unavailable. Use &quot;See all listings on map&quot; below to open the interactive map.
              </Typography>
            </Box>
          )}
        </Box>
        {heroMapboxStaticUrl && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '6px 10px', px: 1.5, py: 0.75, flexShrink: 0, borderTop: '1px solid #e5e7eb', background: '#fafafa' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <HeroMapSubjectPin size={14} />
              <Typography component="span" sx={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.2 }}>Subject property</Typography>
            </Box>
            {heroNeighborhoodPolygons.length > 0 && (
              <>
                <Typography component="span" sx={{ fontSize: '11px', color: '#d1d5db', lineHeight: 1 }}>·</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <NeighbourhoodMapHexIcon />
                  <Typography component="span" sx={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.2 }}>Neighbourhood</Typography>
                </Box>
              </>
            )}
            <Typography component="span" sx={{ fontSize: '11px', color: '#d1d5db', lineHeight: 1 }}>·</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Box sx={{ width: '9px', height: '9px', borderRadius: '50%', background: '#FF2079', boxShadow: '0 0 0 2px rgba(255,32,121,0.3)', flexShrink: 0 }} />
              <Typography component="span" sx={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.2 }}>Similar listings (context)</Typography>
            </Box>
            <Typography component="span" sx={{ fontSize: '11px', color: '#d1d5db', lineHeight: 1 }}>·</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Box sx={{ width: '9px', height: '9px', borderRadius: '50%', background: '#757575', boxShadow: '0 0 0 2px rgba(117,117,117,0.3)', flexShrink: 0 }} />
              <Typography component="span" sx={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.2 }}>Other nearby</Typography>
            </Box>
          </Box>
        )}
        <Box
          sx={{
            px: 1.5,
            py: 1.25,
            flexShrink: 0,
            borderTop: '1px solid #e5e7eb',
            background: '#fafafa',
          }}
        >
          <Typography
            component="div"
            sx={{
              fontSize: '0.85rem',
              color: '#4b5563',
              lineHeight: 1.45,
            }}
          >
            {!traceabilityAvailable ? (
              <>
                Nearby listings are shown for context and benchmarking — they do not directly determine this estimate
                unless you select &quot;Reference comps.&quot;
              </>
            ) : (
              <>
                The highlighted hex (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '2px',
                    verticalAlign: 'middle',
                    mx: '2px',
                  }}
                >
                  <NeighbourhoodMapHexIcon size={12} />
                </Box>
                ) shows the neighbourhood area used for the location adjustment. Nearby listings are shown for context and
                benchmarking — they do not directly determine this estimate unless you select &quot;Reference comps.&quot;
              </>
            )}
          </Typography>
        </Box>
      </Box>
      <Box
        onClick={() => setCompsModalOpen(true)}
        sx={{
          display: 'flex', alignItems: 'center', alignSelf: 'center', gap: '6px',
          mt: '10px', py: '8px', px: '16px', background: '#037A90', borderRadius: '20px',
          cursor: 'pointer', width: 'fit-content',
          transition: 'background 0.15s, box-shadow 0.15s',
          '&:hover': { background: '#026b7d', boxShadow: '0 2px 12px rgba(3,122,144,0.3)' },
        }}
      >
        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>
          See all {reportData.comps?.length || ''} listings on map
        </Typography>
        <Box component="span" sx={{ fontSize: '14px', color: '#fff', lineHeight: 1, ml: '2px' }}>→</Box>
      </Box>
    </Box>
  );

  // Shared percentile selector
  const PercentileSelector = ({ white }: { white?: boolean }) => {
    const RESET_OVERRIDE_VALUE = '__reset_override__';
    /**
     * MUI's Select invokes `renderValue` unconditionally when provided; returning
     * `undefined` blanks the trigger. Only pass it when we actually want to
     * override the displayed label (i.e. when a hero override is active).
     */
    const selectProps = isHeroCustom
      ? { renderValue: () => 'Custom override' as React.ReactNode }
      : {};
    return (
      <FormControl size="small" sx={{ minWidth: 0 }}>
        <Select
          value={selectedKpiPercentile}
          {...selectProps}
          onChange={(e) => {
            const value = e.target.value;
            if (value === RESET_OVERRIDE_VALUE) {
              resetHeroOverrides();
              return;
            }
            if (value === 'comps' && !('comps' in reportData.kpi)) {
              handleCompsUpdate();
            } else {
              setSelectedKpiPercentile(value as KpiPercentileType);
            }
          }}
          sx={{
            fontSize: '0.9rem', fontWeight: 600,
            color: white ? '#fff' : '#037A90',
            '& .MuiSelect-select': { padding: '2px 24px 2px 8px' },
            '& .MuiOutlinedInput-notchedOutline': { border: white ? '1px solid rgba(255,255,255,0.3)' : 'none' },
            '&:hover .MuiOutlinedInput-notchedOutline': { border: white ? '1px solid rgba(255,255,255,0.6)' : '1px solid #037A90' },
            '& .MuiSvgIcon-root': white ? { color: '#fff' } : {},
            ...(white ? { borderRadius: '20px', background: 'rgba(255,255,255,0.12)' } : {}),
          }}
        >
          {isHeroCustom && (
            <MenuItem value={RESET_OVERRIDE_VALUE} sx={{ fontWeight: 600, color: '#037A90' }}>
              Reset custom override
            </MenuItem>
          )}
          <MenuItem value={'25'}>Underperforming (25th)</MenuItem>
          <MenuItem value={'50'}>Median (50th)</MenuItem>
          <MenuItem value={'75'}>Top Performing (75th)</MenuItem>
          <MenuItem value={'comps'}>Reference Comps ({selectedCompsCount} selected)</MenuItem>
          {isEditedKpiAvailable && <MenuItem value={'edited'}>Custom</MenuItem>}
        </Select>
      </FormControl>
    );
  };

  const HeroSection = (
    <Grid container item xs={12}>
      <Box sx={{ width: '100%' }}>
        {/* Highlight — Variation B gradient banner */}
        <Box sx={{
          background: 'linear-gradient(135deg, #037A90 0%, #025e6e 50%, #014d5a 100%)',
          borderRadius: '16px',
          padding: { xs: '1.5rem 1.25rem', md: '2.5rem 2.5rem' },
          mt: { xs: '1.25rem', md: '0.5rem' },
          mb: '1rem',
          position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{ position: 'absolute', top: '-40px', right: '-40px', width: '160px', height: '160px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
          <Box sx={{ position: 'absolute', bottom: '-60px', left: '-30px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />

          <Box sx={{ position: 'absolute', top: '16px', right: '16px', display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: '4px', background: 'rgba(255,255,255,0.12)', borderRadius: '16px', px: '10px', py: '4px' }}>
            <CalendarTodayIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }} />
            <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
              {reportData.primary_input.created_at || '--'}
            </Typography>
          </Box>

          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '2px', mb: '0.35rem' }}>
            Short-term Rental Report
          </Typography>
          <Typography sx={{
            fontSize: { xs: '1.4rem', md: '1.75rem' }, fontWeight: 700, color: '#fff',
            lineHeight: 1.3, letterSpacing: '-0.015em', mb: '0.25rem',
          }}>
            {streetName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 10px', mb: '1.5rem' }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', letterSpacing: '1px' }}>
              {bedrooms}BD · {reportData.primary_input.bathrooms || 0}BA · {reportData.primary_input.accommodates || 0} Guests
            </Typography>
            <Box
              role="button"
              tabIndex={0}
              onClick={() => setFilterEditModalOpen(true)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setFilterEditModalOpen(true);
                }
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
                fontWeight: 600,
                color: 'rgba(255,255,255,0.85)',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
              aria-label="Edit bedrooms, bathrooms, and guests"
            >
              <EditOutlinedIcon sx={{ fontSize: '16px' }} />
              Edit
            </Box>
          </Box>

          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.5px', mb: '0.25rem' }}>
            Estimated Annual Revenue
          </Typography>
          <Typography sx={{
            fontSize: { xs: '2.75rem', md: '3.5rem' }, fontWeight: 800, color: '#fff',
            lineHeight: 1, mb: '0.35rem', letterSpacing: '-0.02em',
          }}>
            {effectiveRevenue ? formatDisplayCurrency(currency, effectiveRevenue) : '--'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', mt: '0.5rem', mb: '1rem' }}>
            <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)' }}>/year</Typography>
            <PercentileSelector white />
          </Box>

          <Typography sx={{ mt: 1.5, fontSize: '0.78rem', color: 'rgba(255,255,255,0.68)', lineHeight: 1.55, maxWidth: '720px' }}>
            {isHeroCustom
              ? 'Custom revenue estimate based on your Occupancy Rate and Nightly Rate inputs (revenue = nightly rate × occupancy × 365).'
              : selectedKpiPercentile === 'comps'
                ? 'Based on the average revenue of your selected comparable listings.'
                : !traceabilityAvailable
                  ? 'Estimated from similar nearby listings, based on the performance of comparable properties in this area.'
                  : 'Estimated using market-wide performance for the selected scenario, then adjusted for neighbourhood and property features.'}
          </Typography>
        </Box>

        {/* Hero: KPIs left, map right (desktop) */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: '1.5rem', mb: '2.25rem', alignItems: 'stretch',
        }}>
          <Box sx={{
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px',
            padding: '1.25rem 1.5rem', boxSizing: 'border-box',
            height: { md: '100%' }, display: 'flex', flexDirection: 'column',
            minHeight: { xs: 'auto', md: '320px' },
          }}>
            <>
                <Box sx={{ flex: '1 1 auto' }}>
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: { xs: '0.5rem', sm: '1rem' },
                    mb: '1rem',
                  }}>
                    <HeroEditableStat
                      label="Occupancy Rate"
                      unit="%"
                      accent="#059669"
                      value={effectiveOccupancy != null ? Math.round(effectiveOccupancy) : null}
                      displayValue={effectiveOccupancy != null ? `${Math.round(effectiveOccupancy)}%` : '--'}
                      marketText={marketOccupancy ? `${marketOccupancy}%` : null}
                      percentiles={{
                        p25: reportData.kpi?.[25]?.ltm_occupancy_rate ?? null,
                        p50: reportData.kpi?.[50]?.ltm_occupancy_rate ?? null,
                        p75: reportData.kpi?.[75]?.ltm_occupancy_rate ?? null,
                      }}
                      isOverridden={heroOverrides.ltm_occupancy_rate != null}
                      canEdit={isUserSubscribed}
                      onApply={v =>
                        setHeroOverrides(prev => ({ ...prev, ltm_occupancy_rate: v }))
                      }
                      onReset={() =>
                        setHeroOverrides(prev => ({ ...prev, ltm_occupancy_rate: null }))
                      }
                    />
                    <HeroEditableStat
                      label="Nightly Rate"
                      unit="$"
                      accent="#7c3aed"
                      value={effectiveADR != null ? Math.round(effectiveADR) : null}
                      displayValue={effectiveADR != null ? formatDisplayCurrency(currency, effectiveADR) : '--'}
                      marketText={
                        marketAdr
                          ? `${currencySymbol}${formatCurrency(
                              String(Math.round(convertCurrency(currency, marketAdr)))
                            )}`
                          : null
                      }
                      percentiles={{
                        p25: reportData.kpi?.[25]?.ltm_nightly_rate ?? null,
                        p50: reportData.kpi?.[50]?.ltm_nightly_rate ?? null,
                        p75: reportData.kpi?.[75]?.ltm_nightly_rate ?? null,
                      }}
                      isOverridden={heroOverrides.ltm_nightly_rate != null}
                      canEdit={isUserSubscribed}
                      onApply={v =>
                        setHeroOverrides(prev => ({ ...prev, ltm_nightly_rate: v }))
                      }
                      onReset={() =>
                        setHeroOverrides(prev => ({ ...prev, ltm_nightly_rate: null }))
                      }
                    />
                    {(() => {
                      const stat = {
                        label: 'Neighbourhood adjustment',
                        value: !isUserSubscribed
                          ? '+8%'
                          : neighborhoodPremium != null && !Number.isNaN(Number(neighborhoodPremium))
                            ? `${Number(neighborhoodPremium) >= 0 ? '+' : ''}${formatNeighborhoodPremiumPct(Number(neighborhoodPremium))}%`
                            : '--',
                        accent: '#f59e0b',
                        neighbourhood: true,
                      };
                      return (
                        <Box
                          key={stat.label}
                          sx={{
                            background: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '12px',
                            padding: { xs: '0.65rem 0.5rem', sm: '1.1rem 1.15rem' },
                            position: 'relative',
                            overflow: 'hidden',
                            minWidth: 0,
                          }}
                        >
                          <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '3px', background: stat.accent }} />
                          <Typography sx={{ fontSize: { xs: '0.62rem', sm: '0.75rem' }, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: { xs: '0.2px', sm: '0.5px' }, mb: '0.35rem', lineHeight: 1.2 }}>
                            {stat.label}
                          </Typography>
                          {stat.neighbourhood && !isUserSubscribed && stat.value !== '--' ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                              <Typography sx={{ fontSize: { xs: '1.05rem', sm: '1.5rem' }, fontWeight: 800, color: '#111827', lineHeight: 1.1, filter: 'blur(8px)', userSelect: 'none' }}>
                                {stat.value}
                              </Typography>
                              <Button
                                variant="text"
                                size="small"
                                onClick={() => handleLoginOrPricingModalOpen('open')}
                                sx={{ textTransform: 'none', fontSize: { xs: '0.65rem', sm: '0.75rem' }, minWidth: 'auto', py: 0, px: 0, fontWeight: 600, color: '#037A90' }}
                              >
                                Upgrade
                              </Button>
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: { xs: '1.05rem', sm: '1.5rem' }, fontWeight: 800, color: '#111827', lineHeight: 1.1, wordBreak: 'break-word' }}>
                              {stat.value}
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}
                  </Box>
                  <Box sx={{ width: '100%', mt: '0.25rem', pt: '0.75rem', borderTop: '1px solid #f3f4f6' }}>
                    {heroSummaryRevenueExplainCallout}
                  </Box>
                </Box>
            </>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontSize: '0.85rem', color: '#4b5563', mt: 'auto', pt: '0.75rem', flexShrink: 0 }}>
              <Box component="span" sx={{ display: { xs: 'inline-flex', md: 'none' }, alignItems: 'center', gap: '0.4rem' }}>
                <CalendarTodayIcon sx={{ color: '#037A90', fontSize: '16px' }} />
                <strong>Created on:</strong> {reportData.primary_input.created_at || '--'}
              </Box>
            </Box>
          </Box>

          <Box sx={{ minHeight: 0, display: 'flex', flexDirection: 'column', height: { md: '100%' } }}>
            {HeroMapCard}
          </Box>
        </Box>
      </Box>
    </Grid>
  );

  const SeasonalityBarChart = grapgDataArray.length > 0 ? (
    <Grid container item xs={12}>
      <Box sx={{
        width: '100%', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px',
        padding: '1.25rem 1.5rem',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.35rem', mb: '0.5rem' }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#1f2937' }}>
            Revenue Seasonality
          </Typography>
          <Tooltip title="Revenue seasonality is based on market-wide data for this area. Each month compares typical listing revenue to this market's 12-month average, so you can see when demand is usually stronger or weaker during the year. It is not derived only from the comparables on your map." placement="top">
            <HelpOutlineRoundedIcon sx={{ fontSize: '16px', color: '#4b5563', cursor: 'help' }} />
          </Tooltip>
        </Box>
        <Box sx={{ position: 'relative' }}>
        <Box sx={{ display: 'flex', gap: '1.5rem', mb: '1rem' }}>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, letterSpacing: '0.5px', mb: '2px' }}>
              YoY revenue change
            </Typography>
            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: !isUserSubscribed ? '#059669' : (trend && trend !== 'loading' ? (trend > 0 ? '#059669' : '#dc2626') : '#111827') }}>
              {!isUserSubscribed ? '+6%' : (trend && trend !== 'loading' ? `${trend > 0 ? '+' : ''}${trend}%` : '--')}
            </Typography>
          </Box>
        </Box>
        <Typography sx={{ fontSize: '0.8rem', color: '#4b5563', mb: '0.75rem' }}>
          Estimated monthly revenue (last 12 months)
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '160px', width: '100%' }}>
          {(() => {
            const mockMonthlyRevenues = [3200, 2800, 3600, 4100, 5200, 5800, 6100, 6400, 5500, 4300, 3500, 3100];
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

            const rawValues = !isUserSubscribed
              ? mockMonthlyRevenues
              : grapgDataArray.map(item => {
                  const raw = item.Revenue ?? 0;
                  return typeof raw === 'number' ? raw : 0;
                });
            const marketTotal = rawValues.reduce((a, b) => a + b, 0);
            const scaleFactor = (!isUserSubscribed || !propRevenue || marketTotal <= 0) ? 1 : propRevenue / marketTotal;
            const values = rawValues.map(v => v * scaleFactor);
            const maxVal = Math.max(...values, 1);

            const displayMonths = !isUserSubscribed ? months : grapgDataArray.map(item => item.timePeriod);

            return values.map((val, i) => {
              const heightPct = Math.max((val / maxVal) * 100, 3);
              const displayVal = val ? formatDisplayCurrency(currency, val) : '--';
              const monthLabel = String(displayMonths[i] ?? '');

              return (
                <Tooltip
                  key={monthLabel || i}
                  title={
                    <SeasonalityBarTooltipContent monthLabel={monthLabel} formattedAmount={displayVal} />
                  }
                  placement="top"
                  enterNextDelay={200}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: 'transparent',
                        boxShadow: 'none',
                        p: 0,
                        maxWidth: 'none',
                        border: 'none',
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      height: '100%',
                      justifyContent: 'flex-end',
                      cursor: 'default',
                      outline: 'none',
                    }}
                  >
                    <Typography sx={{ fontSize: '9px', color: '#1f2937', mb: '4px', whiteSpace: 'nowrap' }}>
                      {displayVal}
                    </Typography>
                    <Box
                      sx={{
                        width: '100%',
                        maxWidth: '48px',
                        height: `${heightPct}%`,
                        minHeight: '4px',
                        background: '#037A90',
                        borderRadius: '4px 4px 0 0',
                        opacity: 0.85,
                      }}
                    />
                    <Typography sx={{ fontSize: '11px', color: '#4b5563', mt: '6px' }}>
                      {monthLabel}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            });
          })()}
        </Box>
        {!isUserSubscribed && (
          <DisabledBlurCard
            subscriptionType="Expert"
            onDisabledOptionClick={() => handleLoginOrPricingModalOpen('open')}
            addBlur="2px"
          />
        )}
        </Box>
        {!isUserSubscribed && (
          <Typography sx={{ fontSize: '11px', color: '#6b7280', textAlign: 'center', mt: 1, lineHeight: 1.45 }}>
            See month-by-month patterns with Expert.
          </Typography>
        )}
      </Box>
    </Grid>
  ) : null;

  const chipSx = {
    fontSize: '12px', border: '1px solid #e5e7eb', borderRadius: '16px',
    padding: '3px 10px', color: '#1f2937', cursor: 'pointer',
    transition: 'all 0.15s ease',
    '&:hover': { background: '#f3f4f6', borderColor: '#037A90' },
  };

  const SidebarInputCard = (
    <>
      <Box sx={{
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px',
        padding: '16px', width: '100%',
      }}>
        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.5px', mb: '8px' }}>
          Property Details
        </Typography>

        <Typography
          onClick={() => setSidebarEditModalOpen(true)}
          sx={{ fontSize: '13px', fontWeight: 600, color: '#1f2937', mb: '10px', wordBreak: 'break-word', cursor: 'pointer', '&:hover': { color: '#037A90' } }}
        >
          {reportData.primary_input.address_full}
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', mb: '12px' }}>
          <Box sx={chipSx} onClick={() => setSidebarEditModalOpen(true)}>
            {reportData.primary_input.bedrooms} 🛏️
          </Box>
          <Box sx={chipSx} onClick={() => setSidebarEditModalOpen(true)}>
            {reportData.primary_input.bathrooms} 🚽
          </Box>
          <Box sx={chipSx} onClick={() => setSidebarEditModalOpen(true)}>
            {reportData.primary_input.accommodates} 👥
          </Box>
        </Box>

        <Box
          onClick={() => setSidebarEditModalOpen(true)}
          sx={{
            display: 'inline-block', fontSize: '11px', fontWeight: 600, color: '#037A90',
            border: '1px solid #037A90', borderRadius: '16px', padding: '2px 10px',
            textTransform: 'capitalize', mb: '12px', cursor: 'pointer',
            '&:hover': { background: '#f0fdfa' },
          }}
        >
          {reportType}
        </Box>

        {reportData.primary_input.property_image_urls && (
          <Box sx={{ mb: '12px', borderRadius: '8px', overflow: 'hidden' }}>
            <img
              src={encodeURI(reportData.primary_input.property_image_urls)}
              style={{ width: '100%', maxHeight: '120px', objectFit: 'cover', display: 'block' }}
            />
          </Box>
        )}

        <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
          Created on: {beautifyDate(reportData.primary_input.created_at)}
        </Typography>
      </Box>

      <Modal open={sidebarEditModalOpen} onClose={() => setSidebarEditModalOpen(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          background: '#fff', borderRadius: '16px', boxShadow: 24,
          width: 'clamp(320px, 90vw, 700px)', maxHeight: '85vh', overflowY: 'auto',
          padding: '24px',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '16px' }}>
            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1f2937' }}>
              Edit Property Details
            </Typography>
            <IconButton onClick={() => setSidebarEditModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          {InputSectionElement}
        </Box>
      </Modal>

      {compsModalOpen && (
        <ReportPageMapSectionSafe
          key={compsModalOpen ? 'comps-open' : 'comps-closed'}
          suspenseFallback={reportPageMapSectionModalFallback}
          reportId={reportData.primary_input.id}
          lat={reportData.primary_input.latitude}
          lng={reportData.primary_input.longitude}
          radius={reportData.primary_input.radius}
          comps={reportData.comps}
          currency={currency}
          handleReport={handleReport}
          isUserReportOwner={isUserReportOwner}
          trackEditOpened={trackEditOpened}
          trackEditPerformed={trackEditPerformed}
          isOnboardingComplete={isOnboardingComplete}
          isUserSubscribed={isUserSubscribed}
          handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
          reportAccessType={reportAccessType}
          kpiData={kpiData}
          compsStatus={compsStatus}
          handleOnSampleClick={handleOnSampleClick}
          ota={ota}
          setSelectedKpiPercentile={setSelectedKpiPercentile}
          matches={matches}
          reportType={reportType}
          initialEditMode={true}
          onEditModalClose={() => setCompsModalOpen(false)}
        />
      )}

      {filterEditModalOpen && (
        <RepotPageInputSection
          key="filter-edit-auto-open"
          reportInputs={reportInputsWithEffectiveType}
          currentPropertyPriceOrRentPrice={getCurrentPropertyOrRentPrice()}
          handleReport={handleReport}
          currency={currency}
          isUserReportOwner={isUserReportOwner}
          trackEditOpened={trackEditOpened}
          trackEditPerformed={trackEditPerformed}
          isOnboardingComplete={isOnboardingComplete}
          isUserSubscribed={isUserSubscribed}
          handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
          loginCountryCode={loginCountryCode}
          selectedKpiPercentile={selectedKpiPercentile}
          setSelectedKpiPercentile={setSelectedKpiPercentile}
          selectedCompsCount={selectedCompsCount}
          isCompsKpiPresent={'comps' in reportData.kpi}
          handleCompsUpdate={handleCompsUpdate}
          compsStatus={compsStatus}
          isEditedKpiAvailable={isEditedKpiAvailable}
          autoOpenFilter={true}
          onFilterClose={() => setFilterEditModalOpen(false)}
        />
      )}
    </>
  );

  const [topMarkets, setTopMarkets] = useState<any[]>([]);
  const [topMarketsIndex, setTopMarketsIndex] = useState(0);
  const [topMarketsAnimating, setTopMarketsAnimating] = useState(false);
  const [topMarketsSubGroupsUsed, setTopMarketsSubGroupsUsed] = useState<string[]>([]);
  const countryCode = nearestMarketBasicKPIDetails?.countryCode;
  const countryName = countryCode ? (ALL_COUNTRY as any)[countryCode] || countryCode : '';
  const topMarketsRegionLabel =
    topMarketsSubGroupsUsed.length > 0
      ? topMarketsSubGroupsUsed.map(sg => sub_group_legends[sg as keyof typeof sub_group_legends] || sg).join(' and ')
      : countryName;

  useEffect(() => {
    if (!countryCode) {
      setTopMarkets([]);
      setTopMarketsSubGroupsUsed([]);
      return;
    }
    const fetchTopMarkets = async () => {
      try {
        const URL = 'https://niyu2lp99j.execute-api.us-east-2.amazonaws.com/default/getBestMarkets';
        const response = await callApi(
          URL,
          { body: { lock_type: 'v2', country_codes: [countryCode], sort_by: 'supply', is_premium_request: false }, queryParams: { apiType: 'v2' } },
          true, false, '', 'POST', true, undefined, true, true
        );
        if (response?.message?.markets?.length) {
          setTopMarkets(response.message.markets.slice(0, 10).map((m: any) => ({
            name: formatMarketName(m),
            grade: m.market_grade || '--',
            url: m.market_url || '#',
          })));
          if (Array.isArray(response.message.sub_groups_used)) {
            setTopMarketsSubGroupsUsed(response.message.sub_groups_used);
          } else {
            setTopMarketsSubGroupsUsed([]);
          }
        } else {
          setTopMarkets([]);
          setTopMarketsSubGroupsUsed([]);
        }
      } catch (e) {
        setTopMarkets([]);
        setTopMarketsSubGroupsUsed([]);
      }
    };
    fetchTopMarkets();
  }, [countryCode]);

  const TopMarketsCarousel = topMarkets.length > 0 ? (
    <Box sx={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px',
      padding: '16px', width: '100%', mt: '12px', overflow: 'hidden',
    }}>
      <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.5px', mb: '12px' }}>
        Top markets in {topMarketsRegionLabel}
      </Typography>
      <Box sx={{ height: '44px', position: 'relative', overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            position: 'absolute', width: '100%',
            transition: 'all 0.3s ease',
            opacity: topMarketsAnimating ? 0 : 1,
            transform: topMarketsAnimating ? 'translateY(-20px)' : 'translateY(0)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#037A90', background: '#f0fdfa', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {topMarketsIndex + 1}
            </Typography>
            <Link
              href={topMarkets[topMarketsIndex]?.url}
              target="_blank"
              rel="noopener"
              underline="hover"
              sx={{ fontSize: '14px', fontWeight: 600, color: '#1f2937', '&:hover': { color: '#037A90' } }}
            >
              {topMarkets[topMarketsIndex]?.name}
            </Link>
          </Box>
          <Typography sx={{
            fontSize: '14px', fontWeight: 700,
            color: getGradeColor(topMarkets[topMarketsIndex]?.grade),
          }}>
            {topMarkets[topMarketsIndex]?.grade}
          </Typography>
        </Box>
      </Box>
    </Box>
  ) : null;

  useEffect(() => {
    if (topMarkets.length <= 1) return;
    const interval = setInterval(() => {
      setTopMarketsAnimating(true);
      setTimeout(() => {
        setTopMarketsIndex(prev => (prev + 1) % Math.max(topMarkets.length, 1));
        setTopMarketsAnimating(false);
      }, 300);
    }, 3000);
    return () => clearInterval(interval);
  }, [topMarkets]);

  const [reportActionBarElevated, setReportActionBarElevated] = useState(false);
  useEffect(() => {
    const onScroll = () => setReportActionBarElevated(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <Box
        sx={{
          background: '#fff',
          width: '100%',
          minHeight: '100vh',
          pb: { xs: MOBILE_BOTTOM_NAV_CLEARANCE, md: 0 },
        }}
      >
      <Box
        sx={{ maxWidth: '1300px', margin: '0 auto', padding: '20px', paddingTop: '20px' }}
        key={`${reportData.id}-${currency}-${selectedKpiPercentile}-report-page`}
      >
      {/* Action bar — sticky under app header; mobile: full-bleed frosted strip; desktop: card (B+C) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.25, md: 1 },
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          position: 'sticky',
          top: HEADER_HEIGHT,
          zIndex: KPI_NAV_BAR_Z_INDEX,
          mx: { xs: -2.5, md: 0 },
          px: { xs: 1.5, md: 2 },
          py: { xs: 1, md: 1.25 },
          mb: { xs: 1, md: 1.25 },
          borderRadius: { xs: 0, md: '12px' },
          border: {
            xs: '1px solid #e5e7eb',
            md: reportActionBarElevated ? '1px solid #e5e7eb' : '1px solid transparent',
          },
          background: {
            xs: 'rgba(255,255,255,0.97)',
            md: reportActionBarElevated ? 'rgba(255,255,255,0.95)' : 'transparent',
          },
          backdropFilter: {
            xs: 'blur(8px)',
            md: reportActionBarElevated ? 'blur(8px)' : 'none',
          },
          WebkitBackdropFilter: {
            xs: 'blur(8px)',
            md: reportActionBarElevated ? 'blur(8px)' : 'none',
          },
          boxShadow: {
            xs: '0 1px 4px rgba(15, 23, 42, 0.07)',
            md: reportActionBarElevated ? '0 1px 3px rgba(15, 23, 42, 0.06)' : 'none',
          },
          transition: 'background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, backdrop-filter 0.2s ease',
        }}
      >
        <TooltipNew content="New Report">
          <IconButton
            color="primary"
            onClick={() => handleCreateNewReport()}
            size="medium"
            sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            aria-label="New report"
          >
            <AddRoundedIcon />
          </IconButton>
        </TooltipNew>
        <Button
          startIcon={<AddRoundedIcon sx={{ fontSize: '18px' }} />}
          sx={{
            ...styles.filterChipStyle(false, COLOR_PRIMARY),
            borderRadius: '8px',
            borderWidth: '1px',
            width: '180px',
            display: { xs: 'none', md: 'inline-flex' },
          }}
          onClick={() => handleCreateNewReport()}
        >
          New Report
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, md: 0.75 }, ml: { xs: 0, md: 2 } }}>
          <TooltipNew content="Download PDF">
            <IconButton color="primary" onClick={handleDownloadClick} disabled={isPdfDownloadTriggerd}>
              {isPdfDownloadTriggerd ? <CircularProgress size={24} /> : <SaveAltIcon />}
            </IconButton>
          </TooltipNew>
          <TooltipNew content="Export Excel">
            <IconButton color="primary" onClick={() => setExcelExportModalOpen(true)}>
              <TableChartOutlinedIcon />
            </IconButton>
          </TooltipNew>
          <TooltipNew content="Share">
            <IconButton color="primary" onClick={handleShare}>
              <ReplyRoundedIcon sx={{ transform: 'scaleX(-1)' }} />
            </IconButton>
          </TooltipNew>
          <TooltipNew content="API">
            <IconButton color="primary" onClick={handleApiCopy}>
              <CodeIcon />
            </IconButton>
          </TooltipNew>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 'auto' }}>
          <TooltipNew content="Back to Calculator">
            <IconButton color="primary" onClick={() => history.push('/airbnb-calculator')}>
              <ArrowBackIcon />
            </IconButton>
          </TooltipNew>
          <TooltipNew content="Tutorial">
            <IconButton color="primary" onClick={() => setShowTutorial(true)}>
              <HelpOutlineRoundedIcon />
            </IconButton>
          </TooltipNew>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {HeroSection}
      </Grid>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 300px' },
        gap: '2rem',
        alignItems: 'start',
        mt: '1.5rem',
        pt: '1.5rem',
        borderTop: '1px solid #e5e7eb',
      }}>
        <Box sx={{
          minWidth: 0,
          gridColumn: { xs: '1', md: '1' },
          gridRow: { xs: '1', md: '1' },
        }}>
          <Grid container spacing={3}>
            {SeasonalityBarChart}
            {SelectedCompsSummary}
            {PropertyNoteSection}
          </Grid>
        </Box>

        <Box sx={{
          minWidth: 0,
          gridColumn: { xs: '1', md: '1' },
          gridRow: { xs: '2', md: '2' },
        }}>
          <Grid container spacing={3}>
            {PerformanceIndicatorsSection}
          </Grid>
        </Box>

        <Box sx={{
          gridColumn: { xs: '1', md: '2' },
          gridRow: { xs: '3', md: '1 / -1' },
          alignSelf: 'start',
          minWidth: 0,
        }}>
          <Box sx={{ position: { md: 'sticky' }, top: { md: HEADER_HEIGHT } }}>
            <Box
              sx={{
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                padding: '12px',
                width: '100%',
                mb: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AddRoundedIcon />}
                onClick={() => handleCreateNewReport()}
                sx={{
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  fontWeight: 600,
                  borderColor: '#e5e7eb',
                  color: '#1f2937',
                  py: 1.1,
                  '&:hover': { borderColor: '#037A90', background: '#f0fdfa' },
                }}
              >
                New report
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={
                  isPdfDownloadTriggerd ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <SaveAltIcon />
                  )
                }
                onClick={() => handleDownloadClick()}
                disabled={isPdfDownloadTriggerd}
                sx={{
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  fontWeight: 600,
                  borderColor: '#e5e7eb',
                  color: '#1f2937',
                  py: 1.1,
                  '&:hover': { borderColor: '#037A90', background: '#f0fdfa' },
                }}
              >
                Download PDF
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<TableChartOutlinedIcon />}
                onClick={() => setExcelExportModalOpen(true)}
                sx={{
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  fontWeight: 600,
                  borderColor: '#e5e7eb',
                  color: '#1f2937',
                  py: 1.1,
                  '&:hover': { borderColor: '#037A90', background: '#f0fdfa' },
                }}
              >
                Export Excel
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<CodeIcon />}
                onClick={() => handleApiCopy()}
                sx={{
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  fontWeight: 600,
                  borderColor: '#e5e7eb',
                  color: '#1f2937',
                  py: 1.1,
                  '&:hover': { borderColor: '#037A90', background: '#f0fdfa' },
                }}
              >
                API
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<HelpOutlineRoundedIcon />}
                onClick={() => setShowTutorial(true)}
                sx={{
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  fontWeight: 600,
                  borderColor: '#e5e7eb',
                  color: '#1f2937',
                  py: 1.1,
                  '&:hover': { borderColor: '#037A90', background: '#f0fdfa' },
                }}
              >
                Tutorial
              </Button>
            </Box>
            {SidebarInputCard}
            {nearestMarketBasicKPIDetails && (
              <Box
                sx={{
                  background: '#f0f9fa',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  padding: '16px',
                  width: '100%',
                  mt: '12px',
                }}
              >
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1f2937', mb: '8px' }}>
                  Analyze {formatMarketName(nearestMarketBasicKPIDetails)} with Airbtics
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#4b5563', lineHeight: 1.45, mb: '12px' }}>
                  Get revenue estimates, occupancy data, and market insights.
                </Typography>
                <Link
                  href={nearestMarketURL || getDummyMarketURL(dummyOverviewMarket)}
                  target="_blank"
                  underline="none"
                  sx={{
                    display: 'inline-block',
                    background: '#037A90',
                    color: '#fff',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    fontWeight: 600,
                    fontSize: '13px',
                    '&:hover': { background: '#1c8386', color: '#fff' },
                  }}
                >
                  Analyze {formatMarketName(nearestMarketBasicKPIDetails)}
                </Link>
              </Box>
            )}
            {TopMarketsCarousel}
            {nearestMarketBasicKPIDetails?.countryCode === 'US' && (
              <Box sx={{
                background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px',
                padding: '16px', width: '100%', mt: '12px',
              }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.5px', mb: '12px' }}>
                  Useful resources
                </Typography>
                <Box
                  onClick={() => setRealtorModalOpen(true)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    cursor: 'pointer', padding: '8px 12px', borderRadius: '8px',
                    transition: 'all 0.2s ease',
                    '&:hover': { background: '#f0fdfa' },
                  }}
                >
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#037A90' }}>
                    Connect to STR specialised realtor
                  </Typography>
                  <OpenInNew sx={{ fontSize: '14px', color: '#037A90' }} />
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {ota === 'airbnb' && ltrData.status !== 'failed' && (
          <Box sx={{
            minWidth: 0,
            gridColumn: { xs: '1', md: '1' },
            gridRow: { xs: '4', md: '3' },
          }}>
            <Grid container spacing={3}>
              {LTRMarketMetricsSection}
            </Grid>
          </Box>
        )}
      </Box>

      </Box>
      </Box>

      {isPdfDownloadTriggerd && (
        <Grid
          container
          xs
          sx={{
            background: '#F3FAFB',
            top: '-10000px',
            width: '710px',
            minWidth: '710px',
            paddingRight: '40px',
            paddingBottom: '40px',
          }}
          spacing={4}
          ref={pdfRef}
          key={`${reportData.id}-${currency}-${selectedKpiPercentile}-report-page-pdf-download`}
        >
          {InputSectionElement}
          {MarketMetricsSection}
          {DemoMarketStatisticButton()}
          <Grid xs={12} container justifyContent={'center'} alignItems={'center'}>
            <Typography variant="body2" sx={{ my: '20px' }}>
              Report generated by <span style={{ color: COLOR_PRIMARY_1 }}>airbtics.com</span> –
              Accurate Airbnb Analytics.
            </Typography>
          </Grid>
        </Grid>
      )}

      {showTutorial && (
        <TutorialModal
          open={showTutorial}
          onClose={() => setShowTutorial(false)}
          type="calculator_general"
          noProgressBar={true}
        />
      )}

      {showCalculatorWelcome && !showTutorial && (
        <TutorialModal
          open={showCalculatorWelcome}
          onClose={dismissCalculatorWelcome}
          type="calculator_general"
          noProgressBar
          headerTitle="New Airbtics Calculator"
          headerDescription={
            'We’ve refreshed the calculator with a clearer report layout, revenue walkthrough, and quick actions. ' +
            'Watch this short tour to get oriented—you can reopen the full tutorial anytime from the toolbar.'
          }
          continueButtonLabel="Continue to report"
          suppressOtherTutorials
        />
      )}

      {/* Action bar modals */}
      <ShareLinkModal
        open={copyLinkModalOpen}
        setOpen={setCopyLinkModalOpen}
        shareUrl={`https://app.airbtics.com/${reportData.primary_input.url_path}`}
        title={copyModalContent?.title}
        copyValue={copyModalContent?.copyValue}
      />

      <DownloadReportModal
        open={pdfDownloadModalOpen}
        onClose={() => setPdfDownloadModalOpen(false)}
        onDownload={downloadPdf}
        userName={user.userName || ''}
        isDownloading={isDownloading}
        seasonality={seasonality}
        trend={trend}
        percentile={selectedKpiPercentile}
        hasCompsListings={Array.isArray(reportData.comps) && reportData.comps.length > 0}
        downloadablePdfUrl={downloadablePdfUrl}
        organizationLogo={user.organization_logo_url || null}
        currentReportType={reportType}
      />

      <ExportExcelModal
        open={excelExportModalOpen}
        onClose={() => setExcelExportModalOpen(false)}
        currentReportType={reportType}
        onExport={handleExcelExport}
      />

      {isDownloadingError && (
        <AutohideSnackbar message="Error downloading report. Please try again." type="error" />
      )}

      {propertyNoteImageError != null && (
        <AutohideSnackbar
          key={propertyNoteImageErrorKey}
          message={propertyNoteImageError}
          type="error"
          handleOnCloseSideEffect={() => setPropertyNoteImageError(null)}
        />
      )}

      {RealtorFormModal}
    </>
  );
};

export default React.memo(ReportPage);
